import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftSnackbar from "components/SoftSnackbar";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import FlagLoader from "layouts/flagloader";

import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import { useProjectPermissions } from "../projectPermissions";
import { ProjectCommunicationComposerDialog } from "../components/ProjectCommunicationDialog";
import ProjectLiveChatBadge from "layouts/project-live-chat/ProjectLiveChatBadge";
import { useProjectLiveChat } from "layouts/project-live-chat/useProjectLiveChat";
import VerisoulResultDialog from "../components/VerisoulResultDialog";
import SurveyArmorResultDialog from "../components/SurveyArmorResultDialog";
import StatsHoverTooltip from "../components/StatsHoverTooltip";
import CompleteDeviceMetric from "../CompleteDeviceMetric";
import ProjectTableSortLabel, {
  epcSortValue,
  sortTableRows,
} from "../components/ProjectTableSortLabel";
import { formatCountryOptionLabel } from "utils/countryOptions";
import { hasPermission } from "utils/granularPermissions";
import {
  projectFilterSearchSuffix,
  projectFilterStateFromSearch,
  writeProjectFiltersToParams,
} from "../projectFilterQuery";

const liveStatuses = ["soft_launch", "full_launch", "hard_launch", "live"];

const exportFilenamePart = (value) =>
  String(value || "project-countries")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "project-countries";

const tabStyles = {
  won: { color: "#16a34a", background: "#dcfce7", border: "#86efac" },
  live: { color: "#0ea5e9", background: "#e0f2fe", border: "#7dd3fc" },
  pause: { color: "#f6a800", background: "#fff7df", border: "#ffd666" },
  invoiced: { color: "#2563eb", background: "#dbeafe", border: "#93c5fd" },
  invoice_at_zero: { color: "#475569", background: "#f1f5f9", border: "#cbd5e1" },
  closed: { color: "#ef4444", background: "#fee2e2", border: "#fecaca" },
};

const countryStatusActions = [
  { value: "soft_launch", label: "Soft Launch", color: "#f6a800" },
  { value: "full_launch", label: "Full Launch", color: "#52c41a" },
  { value: "live", label: "Live", color: "#ff4d6d" },
  { value: "pause", label: "Pause", color: "#64748b" },
  { value: "closed", label: "Closed", color: "#475569" },
];

const tableHeaderCellSx = {
  backgroundColor: "#f8f9fa",
  color: "#8392ab",
  fontSize: "12px",
  fontWeight: 400,
  letterSpacing: 0,
  textTransform: "capitalize",
  borderBottom: "1px solid #e9ecef",
  py: 0.75,
  px: 0.35,
  whiteSpace: "normal",
  lineHeight: 1.15,
  verticalAlign: "middle",
};

const tableBodyCellSx = {
  color: "#334155",
  fontSize: "10px",
  fontWeight: 400,
  borderBottom: "1px solid #edf2f7",
  py: 0.4,
  px: 0.35,
  verticalAlign: "middle",
  lineHeight: 1.15,
  overflowWrap: "anywhere",
};

const metricHeaderCellSx = {
  ...tableHeaderCellSx,
  px: 0.15,
  py: 0.55,
  textAlign: "center",
  borderLeft: "1px solid #e9ecef",
};

const metricBodyCellSx = {
  ...tableBodyCellSx,
  px: 0.1,
  py: 0.35,
  textAlign: "center",
  whiteSpace: "nowrap",
  borderLeft: "1px solid #edf2f7",
};

const overviewLabelSx = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#1e293b",
  lineHeight: 1.2,
};

const overviewValueSx = {
  fontSize: "12px",
  fontWeight: 400,
  color: "#64748b",
  lineHeight: 1.25,
  mt: 0,
  overflowWrap: "anywhere",
};

const overviewButtonSx = {
  minHeight: 26,
  height: 26,
  px: 1.25,
  py: 0,
  fontSize: "10px",
  lineHeight: 1,
  textTransform: "none",
  borderRadius: "6px",
  "& .MuiIcon-root": {
    fontSize: "14px !important",
  },
};

const listSearchInputSx = {
  minHeight: "24px !important",
  padding: "1px 8px !important",
  "& input": {
    height: "20px !important",
    fontSize: "10px !important",
  },
  "& input[type='date']": {
    lineHeight: "20px !important",
  },
  "& input[type='date']::-webkit-calendar-picker-indicator": {
    width: "12px",
    height: "12px",
    padding: 0,
    margin: 0,
  },
};

const listSearchControlSx = {
  width: { xs: "100%", sm: "9rem" },
  flex: { xs: "1 1 100%", sm: "0 0 9rem" },
};

const listSearchSelectSx = {
  "--soft-select-height": "24px",
  "--soft-select-value-height": "22px",
  "--soft-select-font-size": "10px",
  "--soft-select-value-px": "8px",
  "--soft-select-text-top": "50%",
  "--soft-select-indicator-padding": "3px",
  "--soft-select-indicator-size": "12px",
  "--soft-select-menu-padding": "4px",
  "--soft-select-option-padding": "4px 8px",
  "--soft-select-option-min-height": "24px",
  "--soft-select-option-font-size": "10px",
};

const emptyBreakdown = {
  "1h": { complete: 0, clicks: 0, v_rtr: null },
  "12h": { complete: 0, clicks: 0, v_rtr: null },
  "24h": { complete: 0, clicks: 0, v_rtr: null },
  overall: { complete: 0, clicks: 0, v_rtr: null },
};

const metricPeriods = [
  { key: "1h", label: "1" },
  { key: "12h", label: "12" },
  { key: "24h", label: "24" },
  { key: "overall", label: "Overall" },
];

const textOrDash = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const formatIrDisplay = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  const whole = Math.floor(number);
  return number - whole > 0.5 ? whole + 1 : whole;
};

const numericValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const epcText = (revenue, breakdown, stats = {}) => {
  const overall = breakdown?.overall || {};
  const clicks = numericValue(overall.clicks ?? stats.total);
  const totalRevenue = numericValue(revenue);

  if (clicks === null || clicks <= 0 || totalRevenue === null) return "-";
  return `$${(totalRevenue / clicks).toFixed(2)}`;
};

const roundedMetricText = (value) => {
  const number = numericValue(value);
  if (number === null) return "-";
  const whole = Math.floor(number);
  return number - whole > 0.5 ? whole + 1 : whole;
};

const loiMetricColor = (average, configured) => {
  const averageValue = numericValue(average);
  const configuredValue = numericValue(configured);

  if (averageValue === null || configuredValue === null) return "#334155";
  return Math.abs(averageValue - configuredValue) <= 5 ? "#15803d" : "#dc2626";
};

const irMetricColor = (average, configured) => {
  const actualValue = numericValue(average);
  const configuredValue = numericValue(configured);

  if (actualValue === null || configuredValue === null) return "#334155";
  return Math.abs(actualValue - configuredValue) <= 10 ? "#15803d" : "#dc2626";
};

function IrMetric({ average, configured }) {
  return (
    <SoftBox component="span" sx={{ color: "#334155", fontWeight: 400 }}>
      <SoftBox
        component="span"
        sx={{
          color: irMetricColor(average, configured),
          fontWeight: numericValue(average) === null ? 400 : 700,
        }}
      >
        {formatIrDisplay(average)}
      </SoftBox>
      /{formatIrDisplay(configured)}
    </SoftBox>
  );
}

IrMetric.propTypes = {
  average: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  configured: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

function LoiMetric({ average, configured }) {
  return (
    <SoftBox component="span" sx={{ color: "#334155", fontWeight: 400 }}>
      <SoftBox
        component="span"
        sx={{
          color: loiMetricColor(average, configured),
          fontWeight: numericValue(average) === null ? 400 : 700,
        }}
      >
        {roundedMetricText(average)}
      </SoftBox>
      /{roundedMetricText(configured)}
    </SoftBox>
  );
}

LoiMetric.propTypes = {
  average: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  configured: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const digitsOnly = (value, maxDigits = 7) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, maxDigits);

const decimalInput = (value, integerDigits = 7, decimalDigits = 2) => {
  const cleaned = String(value || "").replace(/[^\d.]/g, "");
  const [integer = "", ...decimalParts] = cleaned.split(".");
  const nextInteger = integer.slice(0, integerDigits);
  const decimal = decimalParts.join("").slice(0, decimalDigits);

  if (cleaned.includes(".")) return `${nextInteger}${nextInteger ? "." : ""}${decimal}`;
  return nextInteger;
};

const isValidRequiredCpi = (value) => {
  const text = String(value ?? "").trim();
  const amount = Number(text);

  return (
    text !== "" &&
    Number.isFinite(amount) &&
    amount >= 1 &&
    /^\d+(?:\.\d{1,2})?$/.test(text)
  );
};

const toOption = (item) => ({
  value: item.value ?? item.id,
  label: item.label || item.name,
  name: item.name || item.label,
  code: item.iso_code || item.code || "",
  iso_code: item.iso_code || item.code || "",
});

const selectOptionValue = (option) => option?.value ?? option?.id ?? null;

const dateOnlyValue = (value) => {
  if (!value) return "";

  const text = String(value);
  const directDate = text.match(/^(\d{4}-\d{2}-\d{2})/);
  if (directDate) return directDate[1];

  const date = new Date(text.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return "";

  const pad = (item) => String(item).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const filterOptionFromValue = (value, label) => {
  if (value === null || value === undefined || value === "" || !label) return null;

  return {
    value: String(value),
    label: String(label),
    name: String(label),
  };
};

const selectedFilterOptionMatches = (selected, rowOption) => {
  const selectedOptions = Array.isArray(selected) ? selected : selected ? [selected] : [];
  if (!selectedOptions.length) return true;
  if (!rowOption) return false;
  const rowValue = String(rowOption.value || "");
  const rowLabel = String(rowOption.label || rowOption.name || "").toLowerCase();

  return selectedOptions.some((option) => {
    const selectedValue = String(option?.value ?? option?.id ?? "");
    const selectedLabel = String(option?.label || option?.name || "").toLowerCase();
    return rowValue === selectedValue || (selectedLabel && rowLabel === selectedLabel);
  });
};

const mergeFilterOptions = (options, selected) => {
  const merged = [];

  [...(Array.isArray(selected) ? selected : selected ? [selected] : []), ...(Array.isArray(options) ? options : [])].forEach((option) => {
    const normalized = filterOptionFromValue(
      option?.value ?? option?.id,
      option?.label ?? option?.name
    );
    if (!normalized) return;

    if (!merged.some((item) => String(item.value) === String(normalized.value))) {
      merged.push(normalized);
    }
  });

  return merged;
};

const projectCreatedDateMatches = (project, startDate, endDate) => {
  if (!startDate && !endDate) return true;

  const dateValue = dateOnlyValue(project?.created_at || project?.bid_creation_date);
  if (!dateValue) return false;
  if (startDate && dateValue < startDate) return false;
  if (endDate && dateValue > endDate) return false;

  return true;
};

const vendorToOption = (vendor) => {
  const value = vendor?.value ?? vendor?.id;
  if (!value) return null;

  const name = vendor?.name || vendor?.company_name || vendor?.company || `Vendor ${value}`;

  return {
    ...vendor,
    value,
    id: value,
    label: name,
    name,
  };
};

const deviceTypeOptions = [
  { value: "all", label: "All" },
  { value: "mobile", label: "Mobile" },
  { value: "tablet", label: "Tablet" },
  { value: "desktop/laptop", label: "Desktop/Laptop" },
];

const vendorRedirectFields = [
  { key: "complete_redirect_url", label: "Complete Redirect (Status 1)" },
  { key: "terminate_redirect_url", label: "Terminate Redirect (Status 2)" },
  { key: "over_quota_redirect_url", label: "Over Quota Redirect (Status 3)" },
  { key: "quality_terminate_redirect_url", label: "Quality Terminate Redirect (Status 4)" },
];

const vendorRedirectValues = (source = {}) =>
  vendorRedirectFields.reduce(
    (values, field) => ({
      ...values,
      [field.key]: source?.[field.key] || "",
    }),
    {}
  );

const vendorRedirectsChanged = (form) =>
  vendorRedirectFields.some(
    (field) => (form?.[field.key] || "") !== (form?.redirect_baseline?.[field.key] || "")
  );

const quotaText = (value) => {
  const quota = Number(value || 0);
  return Number.isFinite(quota) && quota > 0 ? quota : "QS";
};

const htmlToPlainText = (value) => {
  const text = String(value || "");
  if (!text) return "";

  if (typeof window !== "undefined" && window.DOMParser) {
    const doc = new window.DOMParser().parseFromString(text, "text/html");
    return (doc.body.textContent || "").trim();
  }

  return text.replace(/<[^>]*>/g, "").trim();
};

const currencyText = (value) => {
  const amount = Number(value || 0);
  return `$${amount.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};

const percentageText = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const percentage = Number(value);
  return Number.isFinite(percentage) ? `${percentage.toFixed(2)}%` : "-";
};

const projectTabFromStatus = (status) => {
  const normalized = String(status || "won").toLowerCase();
  if (liveStatuses.includes(normalized)) return "live";
  return ["pause", "invoiced", "invoice_at_zero", "closed"].includes(normalized)
    ? normalized
    : "won";
};

function MetricHoverCard({ label, breakdown = emptyBreakdown }) {
  return (
    <SoftBox
      sx={{
        minWidth: 320,
        p: 1,
        color: "#1e293b",
        backgroundColor: "#fde047",
        borderRadius: "6px",
      }}
    >
      <SoftTypography variant="caption" fontWeight="regular" color="dark" sx={{ fontSize: "12px" }}>
        {label}
      </SoftTypography>
      <SoftBox
        sx={{
          display: "grid",
          gridTemplateColumns: "78px repeat(4, minmax(44px, 1fr))",
          mt: 0.75,
          borderTop: "1px solid rgba(15, 23, 42, 0.2)",
          borderLeft: "1px solid rgba(15, 23, 42, 0.2)",
        }}
      >
        {["", ...metricPeriods.map((period) => period.label)].map((heading, index) => (
          <SoftBox
            key={`heading-${heading || index}`}
            sx={{
              px: 0.5,
              py: 0.45,
              textAlign: index === 0 ? "left" : "center",
              borderRight: "1px solid rgba(15, 23, 42, 0.2)",
              borderBottom: "1px solid rgba(15, 23, 42, 0.2)",
              fontSize: "12px",
              fontWeight: 400,
            }}
          >
            {heading}
          </SoftBox>
        ))}
        {[
          { label: "Complete", key: "complete" },
          { label: "Clicks", key: "clicks" },
          { label: "V.RTR", key: "v_rtr" },
        ].flatMap((row) => [
          <SoftBox
            key={`${row.key}-label`}
            sx={{
              px: 0.5,
              py: 0.45,
              borderRight: "1px solid rgba(15, 23, 42, 0.2)",
              borderBottom: "1px solid rgba(15, 23, 42, 0.2)",
              fontSize: "12px",
              fontWeight: 400,
            }}
          >
            {row.label}
          </SoftBox>,
          ...metricPeriods.map((period) => (
            <SoftBox
              key={`${row.key}-${period.key}`}
              sx={{
                px: 0.5,
                py: 0.45,
                textAlign: "center",
                borderRight: "1px solid rgba(15, 23, 42, 0.2)",
                borderBottom: "1px solid rgba(15, 23, 42, 0.2)",
                fontSize: "10px",
                fontWeight: 400,
              }}
            >
              {breakdown?.[period.key]?.[row.key] ?? "-"}
            </SoftBox>
          )),
        ])}
      </SoftBox>
    </SoftBox>
  );
}

MetricHoverCard.propTypes = {
  label: PropTypes.string.isRequired,
  breakdown: PropTypes.object,
};

function ProjectCountryMetrics() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions = [], userPermission = [] } = useAuth();
  const projectPermissions = useProjectPermissions(permissions, userPermission);
  const { canUseProjectLiveChat, openProjectLiveChat } = useProjectLiveChat();
  const token = localStorage.getItem("authToken");
  const initialProjectFilters = projectFilterStateFromSearch(location.search);
  const canViewProjects = projectPermissions.canViewPage;
  const canUseActions = projectPermissions.canField("actions");
  const canSearchProjects = projectPermissions.canAction("search");
  const canExportProjects = projectPermissions.canAction("export");
  const canCopyVendorLinks = projectPermissions.canAction("copy_vendor_links");
  const canOpenAddVendor = projectPermissions.canAction("open_add_vendor");
  const canSubmitAddVendor = projectPermissions.canAction("submit_add_vendor");
  const canOpenAddCountryLinks = projectPermissions.canAction("open_add_country_links");
  const canSubmitAddCountryLinks = projectPermissions.canAction("submit_add_country_links");
  const canOpenCountryDetails = projectPermissions.canAction("open_country_details");
  const canSubmitCountryDetails = projectPermissions.canAction("submit_country_details");
  const canManageCountryLinkHitGap = projectPermissions.canAction(
    "manage_country_link_hit_gap"
  );
  const canSetCountryStatus = projectPermissions.canSetCountryStatus;
  const allowedCountryStatusActions = countryStatusActions.filter((action) =>
    canSetCountryStatus(action.value)
  );
  const canViewSurveyResponses =
    projectPermissions.canAction("view_survey_responses") &&
    hasPermission(
      projectPermissions.grantedPermissions,
      "projects.survey_responses.pages.project.view"
    );
  const canUploadFinalIds = projectPermissions.canAction("upload_final_ids");
  const canViewUserUrl = projectPermissions.canAction("view_user_url");
  const canCopyUserUrl = projectPermissions.canAction("copy_user_url");
  const canViewVendorUrl = projectPermissions.canAction("view_vendor_url");
  const canViewProjectCode = projectPermissions.canField("project_code");
  const canViewProjectTitle = projectPermissions.canField("project_title");
  const canViewClient = projectPermissions.canField("client");
  const canViewCountry = projectPermissions.canField("country");
  const canViewCountryProjectName = projectPermissions.canField("country_project_name");
  const canViewStudyType = projectPermissions.canField("study_type");
  const canViewProjectType = projectPermissions.canField("project_type");
  const canViewCreatedDate = projectPermissions.canField("created_at");
  const canViewRequiredN = projectPermissions.canField("required_n");
  const canViewCountryQuota = projectPermissions.canField("country_quota");
  const canViewIr = projectPermissions.canField("ir");
  const canViewLoi = projectPermissions.canField("loi");
  const canViewCpi = projectPermissions.canField("cpi");
  const canViewTargeting = projectPermissions.canField("targeting");
  const canViewLiveUrl = projectPermissions.canField("live_url");
  const canViewTestUrl = projectPermissions.canField("test_url");
  const canViewSecure = projectPermissions.canField("is_secure");
  const canViewCaptchaSecurity = projectPermissions.canField("is_captcha_security");
  const canViewDeviceType = projectPermissions.canField("device_type");
  const canViewVendors = projectPermissions.canField("vendors");
  const canViewVendorQuota = projectPermissions.canField("vendor_quota");
  const canViewStatus = projectPermissions.canField("status");
  const canViewStats = projectPermissions.canField("stats");
  const canViewCompleteDeviceBrowser = projectPermissions.canField("complete_device_browser_counting");
  const canViewInternalUserMetrics = projectPermissions.canField("internal_user_metrics");
  const canViewRtr = projectPermissions.canField("rtr");
  const canViewRevenue = projectPermissions.canField("revenue");
  const canViewComments = projectPermissions.canField("comments");
  const canViewVendorRedirectDetails = projectPermissions.canField("vendor_url") && canViewVendorUrl;

  const [bid, setBid] = useState(null);
  const [countries, setCountries] = useState([]);
  const [metricVendors, setMetricVendors] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(initialProjectFilters.search);
  const [listStartDate, setListStartDate] = useState(initialProjectFilters.startDate);
  const [listEndDate, setListEndDate] = useState(initialProjectFilters.endDate);
  const [studyTypeFilter, setStudyTypeFilter] = useState(initialProjectFilters.studyType);
  const [projectTypeFilter, setProjectTypeFilter] = useState(initialProjectFilters.projectType);
  const [clientFilter, setClientFilter] = useState(initialProjectFilters.client);
  const [projectClientFilterOptions, setProjectClientFilterOptions] = useState([]);
  const [projectClientFilterOptionsLoading, setProjectClientFilterOptionsLoading] = useState(false);
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [actionCountry, setActionCountry] = useState(null);
  const [countryDialogOpen, setCountryDialogOpen] = useState(false);
  const [countryDialogMode, setCountryDialogMode] = useState("edit");
  const [countryDialogCountry, setCountryDialogCountry] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkDialogCountry, setLinkDialogCountry] = useState(null);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorOptionsLoading, setVendorOptionsLoading] = useState(false);
  const [vendorForms, setVendorForms] = useState([
    {
      vendor: null,
      vendor_quota: "",
      vendor_clicks_quota: "",
      target_cpi: "",
      ...vendorRedirectValues(),
      redirect_baseline: vendorRedirectValues(),
    },
  ]);
  const [countryForm, setCountryForm] = useState({
    country: null,
    states: [],
    country_project_name: "",
    required_n: "",
    country_quota: "",
    country_clicks_quota: "",
    ir: "",
    loi: "",
    won_cpi: "",
    targeting: "",
    live_url: "",
    test_url: "",
    comments: "",
    is_secure: true,
    is_captcha_security: true,
    is_verisoul_security: false,
    is_surveyarmor_security: false,
    link_hit_gap_seconds: "",
    device_type: ["all"],
  });
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryOptionsLoading, setCountryOptionsLoading] = useState(false);
  const [countryStateOptions, setCountryStateOptions] = useState([]);
  const [countryStateOptionsLoading, setCountryStateOptionsLoading] = useState(false);
  const [quotaSubmitLoading, setQuotaSubmitLoading] = useState(false);
  const [vendorSubmitLoading, setVendorSubmitLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [verisoulResult, setVerisoulResult] = useState(null);
  const [surveyArmorResult, setSurveyArmorResult] = useState(null);
  const [communicationDialog, setCommunicationDialog] = useState(null);
  const [finalIdsDialogOpen, setFinalIdsDialogOpen] = useState(false);
  const [finalIdsStatus, setFinalIdsStatus] = useState({
    value: "accepted",
    label: "Accepted",
  });
  const [finalIdsFile, setFinalIdsFile] = useState(null);
  const [finalIdsPreview, setFinalIdsPreview] = useState(null);
  const [finalIdsResult, setFinalIdsResult] = useState(null);
  const [finalIdsError, setFinalIdsError] = useState("");
  const [finalIdsImportLoading, setFinalIdsImportLoading] = useState(false);
  const [finalIdsConfirmLoading, setFinalIdsConfirmLoading] = useState(false);

  const sourceProjectTab =
    new URLSearchParams(location.search).get("tab") ||
    bid?.project_tab ||
    projectTabFromStatus(bid?.status);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    }),
    [token]
  );

  const showMessage = useCallback((message, color = "success") => {
    setSnackbar({ open: true, message, color });
  }, []);

  const fetchProjectClientFilterOptions = useCallback(async () => {
    if (!token || !canViewClient || !sourceProjectTab) {
      setProjectClientFilterOptions([]);
      return;
    }

    try {
      setProjectClientFilterOptionsLoading(true);
      const params = new URLSearchParams({
        project_filter: "1",
        project_tab: sourceProjectTab,
      });
      const response = await fetch(`${API_URL}/projects/clients?${params.toString()}`, {
        headers: authHeaders,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to fetch project client filters");
      }

      setProjectClientFilterOptions(
        (Array.isArray(data.clients) ? data.clients : [])
          .map((client) =>
            filterOptionFromValue(
              client?.value ?? client?.id,
              client?.label || client?.name || client?.code
            )
          )
          .filter(Boolean)
      );
    } catch (error) {
      setProjectClientFilterOptions([]);
      showMessage(error.message || "Failed to fetch project client filters", "error");
    } finally {
      setProjectClientFilterOptionsLoading(false);
    }
  }, [authHeaders, canViewClient, showMessage, sourceProjectTab, token]);

  const projectFilterVisibility = useMemo(
    () => ({
      canSearch: canSearchProjects,
      canViewCreatedDate,
      canViewStudyType,
      canViewProjectType,
      canViewClient,
    }),
    [canSearchProjects, canViewClient, canViewCreatedDate, canViewProjectType, canViewStudyType]
  );

  const currentProjectFilters = useMemo(
    () => ({
      search: searchInput,
      startDate: listStartDate,
      endDate: listEndDate,
      studyType: studyTypeFilter,
      projectType: projectTypeFilter,
      client: clientFilter,
    }),
    [clientFilter, listEndDate, listStartDate, projectTypeFilter, searchInput, studyTypeFilter]
  );

  const currentProjectFilterSuffix = useMemo(
    () => projectFilterSearchSuffix(currentProjectFilters, projectFilterVisibility),
    [currentProjectFilters, projectFilterVisibility]
  );

  const openCountryCommunicationDialog = useCallback(
    (type, country) => {
      setCommunicationDialog({
        type,
        scope: {
          scopeType: "country",
          projectId: bid?.id || id,
          projectDetailId: country.id,
          projectCode: bid?.quest_code || id,
          projectName: bid?.project_title || bid?.quest_code,
          label: `Project: ${bid?.quest_code || id} / Country: ${
            country.country_name || country.country_code || country.id
          }`,
        },
      });
    },
    [bid?.id, bid?.project_title, bid?.quest_code, id]
  );

  const fetchCountryOptions = useCallback(async () => {
    if (!token) return;

    try {
      setCountryOptionsLoading(true);
      const response = await fetch(`${API_URL}/projects/countries`, {
        headers: authHeaders,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to fetch countries");
      }

      setCountryOptions((data.countries || []).map(toOption));
    } catch (error) {
      showMessage(error.message || "Failed to fetch countries", "error");
    } finally {
      setCountryOptionsLoading(false);
    }
  }, [authHeaders, showMessage, token]);

  const fetchCountryStateOptions = useCallback(async (countryId) => {
    if (!token || !countryId) {
      setCountryStateOptions([]);
      return;
    }

    try {
      setCountryStateOptionsLoading(true);
      const response = await fetch(
        API_URL + '/client-states?client_country_id=' + encodeURIComponent(countryId),
        { headers: authHeaders }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch states');
      }

      setCountryStateOptions((Array.isArray(data.data) ? data.data : []).map(toOption));
    } catch (error) {
      setCountryStateOptions([]);
      showMessage(error.message || 'Failed to fetch states', 'error');
    } finally {
      setCountryStateOptionsLoading(false);
    }
  }, [authHeaders, showMessage, token]);

  const fetchVendorOptions = useCallback(async () => {
    if (!token || !id) return;

    try {
      setVendorOptionsLoading(true);
      const response = await fetch(
        `${API_URL}/projects/${id}/available-vendors?exclude_won=0&exclude_sent_rfq=0&external_only=1&limit=500`,
        { headers: authHeaders }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to fetch vendors");
      }

      setVendorOptions(
        (Array.isArray(data.vendors) ? data.vendors : [])
          .filter((vendor) => String(vendor?.vendor_type || "external").toLowerCase() !== "internal")
          .map(vendorToOption)
          .filter(Boolean)
      );
    } catch (error) {
      showMessage(error.message || "Failed to fetch vendors", "error");
    } finally {
      setVendorOptionsLoading(false);
    }
  }, [authHeaders, id, showMessage, token]);

  const fetchCountryMetrics = useCallback(async () => {
    if (!canViewProjects || !token || !id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/projects/${id}/country-metrics`, {
        headers: authHeaders,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to fetch project country metrics");
      }

      setBid(data.bid || null);
      setCountries(Array.isArray(data.countries) ? data.countries : []);
      setMetricVendors(Array.isArray(data.project_metric_vendors) ? data.project_metric_vendors : []);
    } catch (error) {
      showMessage(error.message || "Failed to fetch project country metrics", "error");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, canViewProjects, id, showMessage, token]);

  useEffect(() => {
    fetchCountryMetrics();
  }, [fetchCountryMetrics]);

  useEffect(() => {
    fetchProjectClientFilterOptions();
  }, [fetchProjectClientFilterOptions]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    writeProjectFiltersToParams(params, currentProjectFilters, projectFilterVisibility);

    const nextSearch = params.toString();
    const currentSearch = new URLSearchParams(location.search).toString();

    if (nextSearch === currentSearch) return;

    navigate(
      {
        pathname: location.pathname,
        search: `?${nextSearch}`,
      },
      { replace: true }
    );
  }, [
    currentProjectFilters,
    location.pathname,
    location.search,
    navigate,
    projectFilterVisibility,
  ]);

  const studyTypeText = bid?.project_type_master?.name || bid?.project_type || "";
  const projectTypeText =
    (typeof bid?.project_type_option === "object"
      ? bid.project_type_option?.name || bid.project_type_option?.label
      : bid?.project_type_option) || "";
  const clientFilterOption = filterOptionFromValue(
    bid?.client_id ?? bid?.client?.id ?? bid?.client_name ?? bid?.client?.name,
    bid?.client_name || bid?.client?.name || ""
  );
  const studyTypeFilterOption = filterOptionFromValue(
    bid?.project_type_id ?? bid?.project_type_master?.id ?? studyTypeText,
    studyTypeText
  );
  const projectTypeFilterOption = filterOptionFromValue(
    bid?.project_type_option_id ??
      (typeof bid?.project_type_option === "object" ? bid.project_type_option?.id : null) ??
      projectTypeText,
    projectTypeText
  );
  const studyTypeFilterOptions = useMemo(
    () => mergeFilterOptions(studyTypeFilterOption ? [studyTypeFilterOption] : [], studyTypeFilter),
    [studyTypeFilter, studyTypeFilterOption]
  );
  const projectTypeFilterOptions = useMemo(
    () => mergeFilterOptions(projectTypeFilterOption ? [projectTypeFilterOption] : [], projectTypeFilter),
    [projectTypeFilter, projectTypeFilterOption]
  );
  const clientFilterOptions = useMemo(
    () =>
      mergeFilterOptions(
        [...projectClientFilterOptions, ...(clientFilterOption ? [clientFilterOption] : [])],
        clientFilter
      ),
    [clientFilter, clientFilterOption, projectClientFilterOptions]
  );
  const projectFiltersMatch =
    (!canViewCreatedDate || projectCreatedDateMatches(bid, listStartDate, listEndDate)) &&
    (!canViewStudyType || selectedFilterOptionMatches(studyTypeFilter, studyTypeFilterOption)) &&
    (!canViewProjectType || selectedFilterOptionMatches(projectTypeFilter, projectTypeFilterOption)) &&
    (!canViewClient || selectedFilterOptionMatches(clientFilter, clientFilterOption));

  const filteredCountries = useMemo(() => {
    if (!projectFiltersMatch) return [];
    if (!canSearchProjects) return countries;

    const search = searchInput.trim().toLowerCase();
    if (!search) return countries;

    return countries.filter((country) =>
      [
        country.country_name,
        country.country_code,
        country.country_project_name,
        country.required_n,
        bid?.quest_code,
        bid?.project_title,
        bid?.client_name,
        bid?.client?.name,
        studyTypeText,
        projectTypeText,
      ]
        .some((value) => String(value ?? "").toLowerCase().includes(search))
    );
  }, [bid, canSearchProjects, countries, projectFiltersMatch, projectTypeText, searchInput, studyTypeText]);

  const metricVendorColumns = useMemo(
    () =>
      Array.from({ length: 4 }, (_, index) => {
        const vendor = metricVendors[index];
        return vendor || { id: `empty-${index}`, name: "Internal Vendor", initial: "-" };
      }),
    [metricVendors]
  );

  const overviewCountry = useMemo(
    () =>
      countries.find((country) =>
        ["ir", "loi", "won_cpi", "targeting"].some(
          (key) => country?.[key] !== null && country?.[key] !== undefined && country?.[key] !== ""
        )
      ) || countries[0] || {},
    [countries]
  );

  const overviewRequiredN = useMemo(
    () =>
      countries.reduce((total, country) => {
        const requiredN = Number(country.required_n || 0);
        return total + (Number.isFinite(requiredN) ? requiredN : 0);
      }, 0),
    [countries]
  );

  const overviewProjectIr = useMemo(() => {
    const values = countries
      .map((country) => numericValue(country?.ir))
      .filter((value) => value !== null);

    if (values.length === 0) return overviewCountry.ir;
    return values.reduce((total, value) => total + value, 0) / values.length;
  }, [countries, overviewCountry.ir]);

  const overviewProjectLoi = useMemo(() => {
    const values = countries
      .map((country) => numericValue(country?.loi))
      .filter((value) => value !== null);

    if (values.length === 0) return overviewCountry.loi;
    return values.reduce((total, value) => total + value, 0) / values.length;
  }, [countries, overviewCountry.loi]);

  const normalizedProjectStatus = String(bid?.status || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  const canShowLiveCountryActions =
    String(bid?.project_tab || "").toLowerCase() === "live" ||
    liveStatuses.includes(normalizedProjectStatus);
  const isWonProject =
    String(sourceProjectTab).toLowerCase() === "won" ||
    projectTabFromStatus(bid?.status) === "won";
  const showEpc = canViewRtr && !isWonProject;
  const showSurveyResponses = canViewSurveyResponses && !isWonProject;
  const showUploadFinalIds =
    canUploadFinalIds &&
    !isWonProject;
  const overviewActions = [
    canExportProjects ? { label: "Export", icon: "download" } : null,
    ...(canShowLiveCountryActions
      ? [
          canCopyVendorLinks ? { label: "Copy Vendor Links", icon: "content_copy" } : null,
          canOpenAddVendor ? { label: "Add Vendor", icon: "person_add" } : null,
          canOpenAddCountryLinks ? { label: "Add Country Links", icon: "add_link" } : null,
        ]
      : []),
    showSurveyResponses ? { label: "Survey Responses", icon: "table_view" } : null,
    showUploadFinalIds ? { label: "Upload Final IDs", icon: "upload_file" } : null,
  ].filter(Boolean);

  const metricSourceForVendor = (country, vendor) => {
    const internalVendors = country.project_metrics?.internal_vendors || [];
    return (
      internalVendors.find((item) => String(item.id) === String(vendor.id)) || {
        count: 0,
        breakdown: emptyBreakdown,
      }
    );
  };

  const handleSort = useCallback((key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  const sortedCountries = useMemo(
    () =>
      sortTableRows(filteredCountries, sortConfig, (country, key) => {
        const metrics = country.project_metrics || {};
        const stats = metrics.stats || {};

        if (key.startsWith("internal_vendor:")) {
          const vendorId = key.split(":")[1];
          return Number(
            (metrics.internal_vendors || []).find(
              (vendor) => String(vendor.id) === String(vendorId)
            )?.count || 0
          );
        }

        switch (key) {
          case "country":
            return country.country_name || country.name || country.country_code;
          case "country_project_name":
            return country.country_project_name;
          case "required_n":
            return numericValue(country.required_n);
          case "country_clicks_quota":
            return numericValue(country.country_clicks_quota);
          case "ir":
            return numericValue(metrics.average_ir) ?? numericValue(country.ir);
          case "loi":
            return numericValue(metrics.average_loi) ?? numericValue(country.loi);
          case "cpi":
            return numericValue(country.won_cpi);
          case "device_browser":
          case "complete":
            return Number(stats.complete || 0);
          case "terminate":
            return Number(stats.terminate || 0);
          case "quota_full":
            return Number(stats.quota_full || 0);
          case "security_terminate":
            return Number(stats.security_terminate || 0);
          case "initiate":
            return Number(stats.initiate || 0);
          case "external_vendors":
            return Number(metrics.vendors?.count || 0);
          case "vm":
            return numericValue(metrics.rtr);
          case "epc":
            return epcSortValue(metrics.revenue, metrics.overall_breakdown, stats);
          case "revenue":
            return numericValue(metrics.revenue);
          case "links":
            return country.internal_live_url || country.internal_test_url;
          case "status":
            return country.status || bid?.status;
          default:
            return null;
        }
      }),
    [bid?.status, filteredCountries, sortConfig]
  );

  const openCountryVendors = (country) => {
    const countryKey = country.country_code || country.country_name || "UNKNOWN";
    navigate(
      `/projects/${bid.id}/countries/${encodeURIComponent(countryKey)}/vendors?tab=${encodeURIComponent(
        sourceProjectTab
      )}${currentProjectFilterSuffix}&detailId=${encodeURIComponent(country.id || "")}`
    );
  };

  const emptyCountryForm = (country = null) => ({
    country,
    states: [],
    link_hit_gap_seconds: String(),
    country_project_name: bid?.project_title || "",
    required_n: "",
    country_quota: "",
    country_clicks_quota: "",
    ir: "",
    loi: "",
    won_cpi: "",
    targeting: "",
    live_url: "",
    test_url: "",
    comments: "",
    is_secure: true,
    is_captcha_security: true,
    is_verisoul_security: false,
    is_surveyarmor_security: false,
    device_type: ["all"],
  });

  const emptyVendorForm = () => ({
    vendor: null,
    vendor_quota: "",
    vendor_clicks_quota: "",
    target_cpi: "",
    is_verisoul_security: false,
    is_surveyarmor_security: false,
    ...vendorRedirectValues(),
    redirect_baseline: vendorRedirectValues(),
  });

  const copyPlainText = async (value, successMessage, emptyMessage = "Copy text is not available.") => {
    if (!value) {
      showMessage(emptyMessage, "error");
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      showMessage(successMessage, "success");
    } catch (error) {
      showMessage("Failed to copy text.", "error");
    }
  };

  const vendorDisplayName = (vendor) =>
    vendor?.name || vendor?.company || `Vendor ${vendor?.vendor_id || ""}`;

  const buildProjectVendorLinksText = (vendorGroups) =>
    vendorGroups
      .map((vendor, vendorIndex) => {
        const countryLinks = (vendor.countries || [])
          .map(
            (countryItem, countryIndex) =>
              `${countryIndex + 1}. ${countryItem.country_name || countryItem.country_code || "Country"}\nProject Name: ${
                countryItem.country_project_name || bid?.project_title || "-"
              }\nLink Id: ${countryItem.link_id || "-"}\nLive Url: ${
                countryItem.live_url || "-"
              }\nTest Url: ${countryItem.test_url || "-"}`
          )
          .join("\n\n");

        return `${vendorIndex + 1}. ${vendorDisplayName(vendor)}\n${countryLinks}`;
      })
      .join("\n\n");

  const copyProjectVendorLinks = async () => {
    if (!canCopyVendorLinks) return;
    if (!bid?.id) return;

    try {
      const response = await fetch(`${API_URL}/projects/${bid.id}/vendor-country-links`, {
        headers: authHeaders,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to fetch vendor links");
      }

      const vendorGroups = Array.isArray(data.vendors) ? data.vendors : [];
      if (!vendorGroups.length) {
        showMessage("Vendor links are not available for this project.", "error");
        return;
      }

      copyPlainText(
        buildProjectVendorLinksText(vendorGroups),
        "Vendor links copied.",
        "Vendor links are not available for this project."
      );
    } catch (error) {
      showMessage(error.message || "Failed to copy vendor links", "error");
    }
  };

  const openAddProjectVendorDialog = () => {
    if (!canOpenAddVendor) return;
    if (!canShowLiveCountryActions) {
      showMessage("Vendors can only be added when the project is in a live status.", "error");
      return;
    }

    setVendorForms([emptyVendorForm()]);
    fetchVendorOptions();
    setVendorDialogOpen(true);
  };

  const openAddCountryDialog = () => {
    if (!canOpenAddCountryLinks) return;
    if (!canShowLiveCountryActions) {
      showMessage("Country links can only be added when the project is in a live status.", "error");
      return;
    }

    setCountryDialogMode("add");
    setCountryDialogCountry(null);
    setCountryForm({
      ...emptyCountryForm(),
      is_secure: true,
      is_verisoul_security: Boolean(bid?.is_verisoul_security),
      is_surveyarmor_security: Boolean(bid?.is_surveyarmor_security),
    });
    fetchCountryOptions();
    setCountryStateOptions([]);
    setCountryDialogOpen(true);
  };

  const exportCountryMetrics = () => {
    if (!canExportProjects || exportLoading) return;
    if (!filteredCountries.length) {
      showMessage("No country data available to export.", "error");
      return;
    }

    try {
      setExportLoading(true);
      const columns = [
        canViewCountry
          ? {
              label: "Country",
              value: (country) =>
                country.country_name && country.country_code
                  ? `${country.country_name} (${country.country_code})`
                  : textOrDash(country.country_name || country.country_code),
            }
          : null,
        canViewCountryProjectName
          ? {
              label: "Country Project Name",
              value: (country) => textOrDash(country.country_project_name),
            }
          : null,
        canViewRequiredN
          ? { label: "Required N", value: (country) => textOrDash(country.required_n) }
          : null,
        canViewCountryQuota
          ? {
              label: "Country Clicks Quota (Cap)",
              value: (country) => textOrDash(country.country_clicks_quota),
            }
          : null,
        canViewIr
          ? {
              label: "IR(%)",
              value: (country) =>
                `${formatIrDisplay(country.project_metrics?.average_ir)}/${formatIrDisplay(
                  country.ir
                )}`,
            }
          : null,
        canViewLoi
          ? {
              label: "LOI(mins)",
              value: (country) =>
                `${roundedMetricText(
                  country.project_metrics?.average_loi
                )}/${roundedMetricText(country.loi)}`,
            }
          : null,
        canViewCpi
          ? {
              label: "CPI",
              value: (country) =>
                country.won_cpi === null ||
                country.won_cpi === undefined ||
                country.won_cpi === ""
                  ? "-"
                  : `$${country.won_cpi}`,
            }
          : null,
        ...(canViewStats
          ? [
              {
                label: "c/QS",
                value: (country) =>
                  `${Number(country.project_metrics?.stats?.complete || 0)}/${quotaText(
                    country.country_quota
                  )}`,
              },
              {
                label: "T",
                value: (country) =>
                  Number(country.project_metrics?.stats?.terminate || 0),
              },
              {
                label: "OQ",
                value: (country) =>
                  Number(country.project_metrics?.stats?.quota_full || 0),
              },
              {
                label: "ST",
                value: (country) =>
                  Number(country.project_metrics?.stats?.security_terminate || 0),
              },
              {
                label: "P",
                value: (country) =>
                  Number(country.project_metrics?.stats?.initiate || 0),
              },
            ]
          : []),
        ...(canViewInternalUserMetrics
          ? metricVendorColumns.map((vendor, index) => ({
              label: `${vendor.name || "Internal Vendor"}${
                vendor.initial && vendor.initial !== "-" ? ` (${vendor.initial})` : ` ${index + 1}`
              }`,
              value: (country) => Number(metricSourceForVendor(country, vendor).count || 0),
            }))
          : []),
        ...(canViewInternalUserMetrics
          ? [
              {
                label: "External Vendors",
                value: (country) =>
                  Number(country.project_metrics?.vendors?.count || 0),
              },
            ]
          : []),
        canViewRtr
          ? {
              label: "V.M",
              value: (country) => percentageText(country.project_metrics?.rtr),
            }
          : null,
        showEpc
          ? {
              label: "EPC",
              value: (country) =>
                epcText(
                  country.project_metrics?.revenue,
                  country.project_metrics?.overall_breakdown,
                  country.project_metrics?.stats
                ),
            }
          : null,
        canViewRevenue
          ? {
              label: "Revenue",
              value: (country) => currencyText(country.project_metrics?.revenue),
            }
          : null,
        showCountryUserUrlColumn
          ? {
              label: "User Live URL",
              value: (country) => country.internal_live_url || "-",
            }
          : null,
        showCountryUserUrlColumn
          ? {
              label: "User Test URL",
              value: (country) => country.internal_test_url || "-",
            }
          : null,
        canViewStatus
          ? {
              label: "Status",
              value: (country) =>
                textOrDash(country.status_label || bid?.project_status_label),
            }
          : null,
      ].filter(Boolean);
      const headers = columns.map((column) => column.label);
      const rows = filteredCountries.map((country) =>
        columns.map((column) => column.value(country))
      );
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      worksheet["!cols"] = headers.map((header, columnIndex) => ({
        wch: Math.min(
          40,
          Math.max(
            String(header).length + 2,
            ...rows.map((row) => String(row[columnIndex] ?? "").length + 2)
          )
        ),
      }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Countries");

      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
      XLSX.writeFile(
        workbook,
        `project-${exportFilenamePart(bid?.quest_code)}-countries-${timestamp}.xlsx`
      );
      showMessage("Country metrics exported successfully.", "success");
    } catch (error) {
      showMessage(error.message || "Failed to export country metrics.", "error");
    } finally {
      setExportLoading(false);
    }
  };

  const finalIdsApiError = (data, fallback) =>
    data?.message ||
    Object.values(data?.errors || {})
      .flat()
      .find(Boolean) ||
    fallback;

  const resetFinalIdsProcessedState = () => {
    setFinalIdsPreview(null);
    setFinalIdsResult(null);
    setFinalIdsError("");
  };

  const openFinalIdsDialog = () => {
    if (!showUploadFinalIds || !token) return;

    setFinalIdsDialogOpen(true);
    setFinalIdsStatus({ value: "accepted", label: "Accepted" });
    setFinalIdsFile(null);
    resetFinalIdsProcessedState();
  };

  const closeFinalIdsDialog = () => {
    if (finalIdsImportLoading || finalIdsConfirmLoading) return;
    setFinalIdsDialogOpen(false);
  };

  const importFinalIdsPreview = async () => {
    if (!bid?.id || !finalIdsStatus || !finalIdsFile) {
      showMessage("Select a final status and Excel file first.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("project_id", bid.id);
    formData.append("final_status", finalIdsStatus.value);
    formData.append("file", finalIdsFile);

    try {
      setFinalIdsImportLoading(true);
      resetFinalIdsProcessedState();
      const response = await fetch(`${API_URL}/projects/final-ids/preview`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success !== 1) {
        throw new Error(finalIdsApiError(data, "Failed to generate Final IDs preview."));
      }

      setFinalIdsPreview(data);
      showMessage("Preview ready. Database has not been updated.", "info");
    } catch (error) {
      setFinalIdsError(error.message || "Failed to generate Final IDs preview.");
      showMessage(error.message || "Failed to generate Final IDs preview.", "error");
    } finally {
      setFinalIdsImportLoading(false);
    }
  };

  const downloadFinalIdsSample = () => {
    try {
      const finalIdsSheet = XLSX.utils.aoa_to_sheet([
        ["RID"],
        ["RID_SAMPLE_001"],
        ["RID_SAMPLE_002"],
      ]);
      finalIdsSheet["!cols"] = [{ wch: 28 }];

      const instructionsSheet = XLSX.utils.aoa_to_sheet([
        ["Final IDs Upload Instructions"],
        ["1. Do not rename or remove the RID column heading."],
        ["2. Enter one RID per row in the first column of the Final IDs sheet."],
        ["3. RID matching is case-insensitive; blank and duplicate RIDs are ignored."],
        ["4. Remove the sample RID rows before uploading your final file."],
        ["5. Save the completed file as XLSX, XLS or CSV before importing."],
      ]);
      instructionsSheet["!cols"] = [{ wch: 82 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, finalIdsSheet, "Final IDs");
      XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");
      XLSX.writeFile(workbook, "Final_IDs_Sample.xlsx");
    } catch (error) {
      setFinalIdsError(error.message || "Failed to download the sample Excel file.");
      showMessage(error.message || "Failed to download the sample Excel file.", "error");
    }
  };

  const confirmFinalIdsUpload = async () => {
    if (!finalIdsPreview?.preview_token) {
      showMessage("Import the Excel file again to generate a preview.", "error");
      return;
    }

    try {
      setFinalIdsConfirmLoading(true);
      setFinalIdsError("");
      const response = await fetch(`${API_URL}/projects/final-ids/confirm`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ preview_token: finalIdsPreview.preview_token }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success !== 1) {
        throw new Error(finalIdsApiError(data, "Failed to confirm Final IDs upload."));
      }

      setFinalIdsResult(data);
      setFinalIdsPreview(null);
      setFinalIdsError("");
      showMessage("Final IDs uploaded successfully.", "success");
    } catch (error) {
      setFinalIdsError(error.message || "Failed to confirm Final IDs upload.");
      showMessage(error.message || "Failed to confirm Final IDs upload.", "error");
    } finally {
      setFinalIdsConfirmLoading(false);
    }
  };

  const handleOverviewAction = (action) => {
    if (action.label === "Export") {
      exportCountryMetrics();
      return;
    }

    if (action.label === "Add Country Links") {
      openAddCountryDialog();
      return;
    }

    if (action.label === "Add Vendor") {
      openAddProjectVendorDialog();
      return;
    }

    if (action.label === "Survey Responses") {
      if (!canViewSurveyResponses) return;
      navigate(`/projects/${bid.id}/survey-responses?tab=${encodeURIComponent(sourceProjectTab)}`);
      return;
    }

    if (action.label === "Upload Final IDs") {
      openFinalIdsDialog();
      return;
    }

    if (action.label === "Copy Vendor Links") {
      copyProjectVendorLinks();
      return;
    }

    showMessage(`${action.label} action is ready. Please confirm the next step.`, "info");
  };

  const projectStatusBadge = () => {
    const style = tabStyles[projectTabFromStatus(bid?.status)] || tabStyles.won;

    return (
      <SoftBox
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 20,
          px: 0.65,
          borderRadius: "999px",
          color: style.color,
          backgroundColor: style.background,
          border: `1px solid ${style.border}`,
          fontSize: 10,
          fontWeight: 400,
          lineHeight: 1.1,
          whiteSpace: "normal",
          textAlign: "center",
        }}
      >
        {textOrDash(bid?.project_status_label)}
      </SoftBox>
    );
  };

  const countryStatusBadge = (country) => {
    const status = country?.status || bid?.status;
    const style = tabStyles[projectTabFromStatus(status)] || tabStyles.won;

    const badge = (
      <SoftBox
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 20,
          px: 0.65,
          borderRadius: "999px",
          color: style.color,
          backgroundColor: style.background,
          border: `1px solid ${style.border}`,
          fontSize: 10,
          fontWeight: 400,
          lineHeight: 1.1,
          whiteSpace: "normal",
          textAlign: "center",
        }}
      >
        {textOrDash(country?.status_label || bid?.project_status_label)}
      </SoftBox>
    );

    return (
      <Tooltip
        title={country?.status_source === "country" ? "Country status" : "Inherited from project status"}
      >
        <span>{badge}</span>
      </Tooltip>
    );
  };

  const overviewItems = [
    canViewProjectCode ? { label: "Project Code", value: bid?.quest_code } : null,
    canViewProjectTitle ? { label: "Project Name", value: bid?.project_title } : null,
    canViewClient ? { label: "Client", value: bid?.client_name || bid?.client?.name } : null,
    projectPermissions.canField("project_manager")
      ? { label: "Project Manager", value: bid?.project_manager?.name || bid?.project_manager?.email }
      : null,
    canViewStatus ? { label: "Project Status", value: projectStatusBadge() } : null,
    canViewStudyType ? { label: "Study Type", value: studyTypeText } : null,
    canViewProjectType ? { label: "Project Type", value: projectTypeText } : null,
    canViewRequiredN ? { label: "Required N", value: overviewRequiredN || "-" } : null,
    canViewIr ? { label: "IR(%)", value: formatIrDisplay(overviewProjectIr) } : null,
    canViewLoi ? { label: "LOI(mins)", value: roundedMetricText(overviewProjectLoi) } : null,
    canViewCpi
      ? {
          label: "CPI",
          value:
            overviewCountry.won_cpi === null ||
            overviewCountry.won_cpi === undefined ||
            overviewCountry.won_cpi === ""
              ? "-"
              : `$${overviewCountry.won_cpi}`,
        }
      : null,
    canViewTargeting
      ? { label: "Targeting", value: htmlToPlainText(overviewCountry.targeting || bid?.targeting) }
      : null,
  ].filter(Boolean);

  const openCountryStatusMenu = (event, country) => {
    if (allowedCountryStatusActions.length === 0) return;
    if (!/^\d+$/.test(String(country?.id || ""))) {
      showMessage("The database row for this country was not found, so status cannot be updated.", "error");
      return;
    }

    setActionCountry(country);
    setActionsAnchor(event.currentTarget);
  };

  const closeCountryStatusMenu = () => {
    setActionsAnchor(null);
    setActionCountry(null);
  };

  const updateCountryStatus = async (status) => {
    if (!canSetCountryStatus(status)) return;
    if (!bid?.id || !actionCountry?.id) return;

    try {
      setStatusLoading(true);
      const response = await fetch(`${API_URL}/projects/${bid.id}/details/${actionCountry.id}/status`, {
        method: "PATCH",
        headers: authHeaders,
        body: JSON.stringify({ status }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to update country status");
      }

      closeCountryStatusMenu();
      showMessage(data.message || "Country status updated successfully", "success");
      fetchCountryMetrics();
    } catch (error) {
      showMessage(error.message || "Failed to update country status", "error");
    } finally {
      setStatusLoading(false);
    }
  };

  const openCountryQuotaDialog = (country) => {
    if (!canOpenCountryDetails) return;
    if (!/^\d+$/.test(String(country?.id || ""))) {
      showMessage("The database row for this country was not found, so quota cannot be edited.", "error");
      return;
    }

    setCountryDialogMode("edit");
    setCountryDialogCountry(country);
    setCountryForm({
      country: toOption({
        value: country?.country_id,
        id: country?.country_id,
        label: country?.country_name,
        name: country?.country_name,
      }),
      states: (Array.isArray(country?.states) ? country.states : []).map(toOption),
      country_project_name: country?.country_project_name || bid?.project_title || "",
      required_n: country?.required_n ?? "",
      country_quota: country?.country_quota ?? "",
      country_clicks_quota: country?.country_clicks_quota ?? "",
      ir: country?.ir ?? "",
      loi: country?.loi ?? "",
      won_cpi: country?.won_cpi ?? "",
      targeting: htmlToPlainText(country?.targeting || ""),
      live_url: country?.live_url || "",
      test_url: country?.test_url || "",
      comments: country?.comments || "",
      is_secure: Boolean(country?.is_secure),
      is_captcha_security: Boolean(country?.is_captcha_security),
      is_verisoul_security: Boolean(country?.is_verisoul_security),
      is_surveyarmor_security: Boolean(country?.is_surveyarmor_security),
      link_hit_gap_seconds:
        country?.link_hit_gap_seconds === null || country?.link_hit_gap_seconds === undefined
          ? ""
          : String(country.link_hit_gap_seconds),
      device_type: Array.isArray(country?.device_type) && country.device_type.length
        ? country.device_type
        : ["all"],
    });
    fetchCountryStateOptions(country?.country_id);
    setCountryDialogOpen(true);
  };

  const closeCountryQuotaDialog = () => {
    if (quotaSubmitLoading) return;
    setCountryDialogOpen(false);
    setCountryDialogCountry(null);
    setCountryDialogMode("edit");
  };

  const openCountryLinksDialog = (country) => {
    if (!canViewUserUrl) return;
    if (!country?.internal_live_url && !country?.internal_test_url) {
      showMessage("Internal user links have not been generated for this country.", "error");
      return;
    }

    setLinkDialogCountry(country);
    setLinkDialogOpen(true);
  };

  const closeCountryLinksDialog = () => {
    setLinkDialogOpen(false);
    setLinkDialogCountry(null);
  };

  const copyLink = async (value, label) => {
    if (!canCopyUserUrl) return;
    if (!value) {
      showMessage(`${label} Url is not available.`, "error");
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      showMessage(`${label} Url copied.`, "success");
    } catch (error) {
      showMessage(`Failed to copy ${label} Url.`, "error");
    }
  };

  const updateCountryForm = (field, value) => {
    const integerFields = [
      "required_n",
      "country_quota",
      "country_clicks_quota",
      "link_hit_gap_seconds",
    ];
    const decimalFields = ["ir", "loi", "won_cpi"];
    let nextValue = value;

    if (integerFields.includes(field)) nextValue = digitsOnly(value, 7);
    if (decimalFields.includes(field)) nextValue = decimalInput(value, 7, 2);

    if (field === 'country') {
      setCountryStateOptions([]);
      fetchCountryStateOptions(selectOptionValue(nextValue));
      setCountryForm((current) => ({
        ...current,
        country: nextValue,
        states: [],
      }));
      return;
    }

    setCountryForm((current) => ({
      ...current,
      [field]: nextValue,
    }));
  };

  const updateVendorForm = (index, field, value) => {
    const integerFields = ["vendor_quota", "vendor_clicks_quota"];
    const nextValue = field === "target_cpi" ? decimalInput(value, 7, 2) : value;

    setVendorForms((current) =>
      current.map((form, formIndex) => {
        if (formIndex !== index) return form;

        if (field === "vendor") {
          const redirects = vendorRedirectValues(value?.redirects || value || {});
          return {
            ...form,
            vendor: value,
            ...redirects,
            redirect_baseline: redirects,
          };
        }

        return {
          ...form,
          [field]: integerFields.includes(field) ? digitsOnly(nextValue, 7) : nextValue,
        };
      })
    );
  };

  const addVendorFormRow = () => {
    setVendorForms((current) => [...current, emptyVendorForm()]);
  };

  const removeVendorFormRow = (index) => {
    setVendorForms((current) =>
      current.length <= 1 ? current : current.filter((_, formIndex) => formIndex !== index)
    );
  };

  const closeVendorDialog = () => {
    if (vendorSubmitLoading) return;
    setVendorDialogOpen(false);
    setVendorForms([emptyVendorForm()]);
  };

  const vendorPayloadFromForm = (form) => {
    const payload = projectPermissions.filterPayloadByFields({
      vendor_id: selectOptionValue(form.vendor),
      vendor_quota: form.vendor_quota === "" ? null : form.vendor_quota,
      vendor_clicks_quota:
        form.vendor_clicks_quota === "" ? null : form.vendor_clicks_quota,
      target_cpi: form.target_cpi === "" ? null : form.target_cpi,
    }, {
      vendor_id: "vendors",
      vendor_quota: "vendor_quota",
      vendor_clicks_quota: "vendor_clicks_quota",
      target_cpi: "target_cpi",
    });
    // Unchecked while adding means inherit country/project; checked creates a
    // direct vendor ON override.
    if (Boolean(form.is_verisoul_security)) {
      payload.is_verisoul_security = true;
    }
    if (Boolean(form.is_surveyarmor_security)) {
      payload.is_surveyarmor_security = true;
    }

    if (projectPermissions.canField("vendor_url") && projectPermissions.canAction("view_vendor_url") && vendorRedirectsChanged(form)) {
      payload.redirects = vendorRedirectValues(form);
    }

    return payload;
  };

  const submitProjectVendor = async () => {
    if (!canSubmitAddVendor) return;
    if (!bid?.id) return;

    const selectedForms = vendorForms.filter((form) => selectOptionValue(form.vendor));
    if (!selectedForms.length || selectedForms.length !== vendorForms.length) {
      showMessage("Vendor selection is required for every row.", "error");
      return;
    }

    const vendorIds = selectedForms.map((form) => String(selectOptionValue(form.vendor)));
    if (new Set(vendorIds).size !== vendorIds.length) {
      showMessage("Please select each vendor only once.", "error");
      return;
    }

    if (selectedForms.some((form) => !isValidRequiredCpi(form.target_cpi))) {
      showMessage(
        "Target CPI is required, must be at least 1, and can have a maximum of 2 decimal places.",
        "error"
      );
      return;
    }

    try {
      setVendorSubmitLoading(true);
      const messages = [];

      for (const form of selectedForms) {
        const response = await fetch(`${API_URL}/projects/${bid.id}/project-vendors`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify(vendorPayloadFromForm(form)),
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.success !== 1) {
          throw new Error(data.message || "Failed to add vendor");
        }

        messages.push(data.message || "Vendor added successfully.");
      }

      showMessage(messages.join(" "), "success");
      setVendorDialogOpen(false);
      setVendorForms([emptyVendorForm()]);
      fetchCountryMetrics();
    } catch (error) {
      showMessage(error.message || "Failed to add vendor", "error");
    } finally {
      setVendorSubmitLoading(false);
    }
  };

  const toggleDeviceType = (device) => {
    setCountryForm((current) => {
      const selected = Array.isArray(current.device_type) ? current.device_type : ["all"];

      if (device === "all") {
        return { ...current, device_type: ["all"] };
      }

      let next = selected.filter((item) => item !== "all");
      next = next.includes(device) ? next.filter((item) => item !== device) : [...next, device];

      return { ...current, device_type: next.length ? next : ["all"] };
    });
  };

  const submitCountryQuota = async () => {
    if (!bid?.id) return;
    const isAddMode = countryDialogMode === "add";
    if (isAddMode && !canSubmitAddCountryLinks) return;
    if (!isAddMode && !canSubmitCountryDetails) return;
    const countryId = selectOptionValue(countryForm.country);

    if (isAddMode && !countryId) {
      showMessage("Country selection is required.", "error");
      return;
    }

    const countryCpiText = String(countryForm.won_cpi ?? "").trim();
    const countryCpi = Number(countryCpiText);
    if (
      countryCpiText === "" ||
      !Number.isFinite(countryCpi) ||
      countryCpi < 1 ||
      !/^\d+(?:\.\d{1,2})?$/.test(countryCpiText)
    ) {
      showMessage(
        "CPI is required, must be at least 1, and can have a maximum of 2 decimal places.",
        "error"
      );
      return;
    }

    const rawGapSeconds = String(countryForm.link_hit_gap_seconds ?? "").trim();
    const gapSeconds = rawGapSeconds === "" ? null : Number(rawGapSeconds);
    if (
      canManageCountryLinkHitGap &&
      gapSeconds !== null &&
      (!Number.isInteger(gapSeconds) || gapSeconds < 1 || gapSeconds > 86400)
    ) {
      showMessage(
        "Link hit gap must be blank or a whole number between 1 and 86400 seconds.",
        "error"
      );
      return;
    }

    try {
      setQuotaSubmitLoading(true);
      const payload = projectPermissions.filterPayloadByFields({
        required_n: countryForm.required_n === "" ? null : countryForm.required_n,
        country_project_name:
          countryForm.country_project_name && countryForm.country_project_name !== bid?.project_title
            ? countryForm.country_project_name
            : null,
        country_quota: countryForm.country_quota === "" ? null : countryForm.country_quota,
        country_clicks_quota:
          countryForm.country_clicks_quota === "" ? null : countryForm.country_clicks_quota,
        ir: countryForm.ir === "" ? null : countryForm.ir,
        loi: countryForm.loi === "" ? null : countryForm.loi,
        won_cpi: countryForm.won_cpi === "" ? null : countryForm.won_cpi,
        targeting: countryForm.targeting || "",
        live_url: countryForm.live_url || null,
        test_url: countryForm.test_url || null,
        comments: countryForm.comments || "",
        is_secure: Boolean(countryForm.is_secure),
        is_captcha_security: Boolean(countryForm.is_captcha_security),
        device_type: Array.isArray(countryForm.device_type) ? countryForm.device_type : ["all"],
      }, {
        required_n: "required_n",
        country_project_name: "country_project_name",
        country_quota: "country_quota",
        country_clicks_quota: "country_clicks_quota",
        ir: "ir",
        loi: "loi",
        won_cpi: "won_cpi",
        targeting: "targeting",
        live_url: "live_url",
        test_url: "test_url",
        comments: "comments",
        is_secure: "is_secure",
        is_captcha_security: "is_captcha_security",
        device_type: "device_type",
      });
      if (canViewCountry) {
        payload.state_ids = (Array.isArray(countryForm.states) ? countryForm.states : [])
          .map(selectOptionValue)
          .filter(Boolean);
      }
      if (canManageCountryLinkHitGap) {
        payload.link_hit_gap_seconds = gapSeconds;
      }
      // Unchecked on add means inherit the project setting. On edit, submit
      // only when the user changed the effective checkbox.
      if (
        (isAddMode &&
          Boolean(countryForm.is_verisoul_security) !==
            Boolean(bid?.is_verisoul_security)) ||
        (!isAddMode &&
          Boolean(countryForm.is_verisoul_security) !==
            Boolean(countryDialogCountry?.is_verisoul_security))
      ) {
        payload.is_verisoul_security = Boolean(countryForm.is_verisoul_security);
      }
      // A new country inherits; only changed values create a direct override.
      if (
        (isAddMode &&
          Boolean(countryForm.is_surveyarmor_security) !==
            Boolean(bid?.is_surveyarmor_security)) ||
        (!isAddMode &&
          Boolean(countryForm.is_surveyarmor_security) !==
            Boolean(countryDialogCountry?.is_surveyarmor_security))
      ) {
        payload.is_surveyarmor_security = Boolean(countryForm.is_surveyarmor_security);
      }
      if (isAddMode && canViewCountry) payload.country_id = countryId;

      const response = await fetch(
        isAddMode
          ? `${API_URL}/projects/${bid.id}/details/project-country`
          : `${API_URL}/projects/${bid.id}/details/${countryDialogCountry.id}/project-quota`,
        {
          method: isAddMode ? "POST" : "PATCH",
          headers: authHeaders,
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to save country link");
      }

      if (data.verisoul_result) {
        setVerisoulResult(data.verisoul_result);
      }
      if (data.surveyarmor_result) {
        setSurveyArmorResult(data.surveyarmor_result);
      }
      showMessage(
        data.verisoul_result ? "Country link saved successfully." : data.message || "Country link saved successfully",
        "success"
      );
      setCountryDialogOpen(false);
      setCountryDialogCountry(null);
      setCountryDialogMode("edit");
      fetchCountryMetrics();
    } catch (error) {
      showMessage(error.message || "Failed to save country link", "error");
    } finally {
      setQuotaSubmitLoading(false);
    }
  };

  if (!canViewProjects) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="body2" color="text">
            You do not have permission to view project country metrics.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  const showCountryUserUrlColumn = false;
  const showCountryRowActions =
    canUseProjectLiveChat ||
    (canUseActions &&
      (canViewComments ||
        projectPermissions.canAction("mail") ||
        projectPermissions.canAction("chat") ||
        canOpenCountryDetails));
  const showCountryStatusActions = canUseActions && allowedCountryStatusActions.length > 0;
  const countryColumnCount =
    [
      canViewCountry,
      canViewCountryProjectName,
      canViewRequiredN,
      canViewCountryQuota,
      canViewIr,
      canViewLoi,
      canViewCpi,
      canViewRtr,
      showEpc,
      canViewRevenue,
      showCountryUserUrlColumn,
      canViewStatus,
      showCountryRowActions,
      showCountryStatusActions,
    ].filter(Boolean).length +
    (canViewStats ? 5 : 0) +
    (canViewCompleteDeviceBrowser ? 1 : 0) +
    (canViewInternalUserMetrics ? 5 : 0);
  const canSaveCountryDialog =
    countryDialogMode === "add" ? canSubmitAddCountryLinks : canSubmitCountryDetails;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Project Countries"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar((current) => ({ ...current, open: false }))}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        time={3000}
      />
      <VerisoulResultDialog result={verisoulResult} onClose={() => setVerisoulResult(null)} />
      <SurveyArmorResultDialog result={verisoulResult ? null : surveyArmorResult} onClose={() => setSurveyArmorResult(null)} />

      <SoftBox px={0} pt="2px" pb={1} mt="-14px">
        <SoftBox display="flex" alignItems="center" gap={0.4} mb={0}>
          <SoftButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() =>
              navigate(`/projects/list?tab=${encodeURIComponent(sourceProjectTab)}${currentProjectFilterSuffix}`)
            }
            sx={{ minWidth: "auto", px: 1.5, py: 0.55, textTransform: "none" }}
          >
            Go Back
          </SoftButton>
          <SoftTypography
            variant="h4"
            fontWeight="bold"
            color="dark"
            sx={{ fontSize: "0.875rem" }}
          >
            Project Countries - {textOrDash(bid?.quest_code)}
          </SoftTypography>
        </SoftBox>

        <Card>
          <SoftBox px={1} pt={0.15} pb={0.2} sx={{ borderBottom: "1px solid #e9ecef" }}>
            <SoftTypography
              variant="h6"
              fontWeight="bold"
              color="dark"
              sx={{ fontSize: "1rem", mb: 0.15 }}
            >
              Overview
            </SoftTypography>
            <SoftBox
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                },
                columnGap: 3,
                rowGap: 0.15,
              }}
            >
              {overviewItems.map((item) => (
                <SoftBox key={item.label} minWidth={0}>
                  <SoftTypography variant="caption" sx={overviewLabelSx}>
                    {item.label}
                  </SoftTypography>
                  <SoftBox sx={overviewValueSx}>
                    {React.isValidElement(item.value) ? item.value : textOrDash(item.value)}
                  </SoftBox>
                </SoftBox>
              ))}
            </SoftBox>
            <SoftBox display="flex" flexWrap="wrap" gap={0.75} mt={0.18}>
              {overviewActions.map((action) => (
                <SoftButton
                  key={action.label}
                  color="info"
                  size="small"
                  sx={{
                    ...overviewButtonSx,
                    ...(action.label === "Upload Final IDs" ? { ml: "auto" } : {}),
                  }}
                  onClick={() => handleOverviewAction(action)}
                  disabled={
                    action.label === "Export" &&
                    (exportLoading || filteredCountries.length === 0)
                  }
                >
                  <Icon sx={{ mr: 0.5 }}>{action.icon}</Icon>
                  {action.label === "Export" && exportLoading ? "Exporting..." : action.label}
                </SoftButton>
              ))}
            </SoftBox>
          </SoftBox>

          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
            gap={0.5}
            px="2px"
            py="2px"
          >
            <SoftBox color="text" fontSize="12px" fontWeight={400}>
              Showing {filteredCountries.length ? 1 : 0} to {filteredCountries.length} of{" "}
              {filteredCountries.length} entries
            </SoftBox>
            {(canSearchProjects ||
              canViewCreatedDate ||
              canViewStudyType ||
              canViewProjectType ||
              canViewClient) && (
              <SoftBox
                display="flex"
                flexWrap="wrap"
                gap={0.75}
                justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                sx={{ ml: { sm: "auto" }, width: { xs: "100%", sm: "auto" } }}
              >
                {canSearchProjects && (
                  <SoftBox sx={listSearchControlSx}>
                    <SoftInput
                      size="small"
                      placeholder="Search..."
                      value={searchInput}
                      onChange={(event) => setSearchInput(event.target.value)}
                      sx={listSearchInputSx}
                    />
                  </SoftBox>
                )}
                {canViewCreatedDate && (
                  <>
                    <SoftBox sx={listSearchControlSx}>
                      <SoftInput
                        size="small"
                        type="date"
                        value={listStartDate}
                        onChange={(event) => setListStartDate(event.target.value)}
                        sx={listSearchInputSx}
                        inputProps={{ "aria-label": "Start Date" }}
                      />
                    </SoftBox>
                    <SoftBox sx={listSearchControlSx}>
                      <SoftInput
                        size="small"
                        type="date"
                        value={listEndDate}
                        onChange={(event) => setListEndDate(event.target.value)}
                        sx={listSearchInputSx}
                        inputProps={{ "aria-label": "End Date" }}
                      />
                    </SoftBox>
                  </>
                )}
                {canViewStudyType && (
                  <SoftBox sx={listSearchControlSx}>
                    <SoftSelect
                      size="small"
                      compactMenu
                      placeholder="Study Type"
                      value={studyTypeFilter}
                      options={studyTypeFilterOptions}
                      onChange={setStudyTypeFilter}
                      isSearchable
                      isClearable
                      isMulti
                      sx={listSearchSelectSx}
                    />
                  </SoftBox>
                )}
                {canViewProjectType && (
                  <SoftBox sx={listSearchControlSx}>
                    <SoftSelect
                      size="small"
                      compactMenu
                      placeholder="Project Type"
                      value={projectTypeFilter}
                      options={projectTypeFilterOptions}
                      onChange={setProjectTypeFilter}
                      isSearchable
                      isClearable
                      isMulti
                      sx={listSearchSelectSx}
                    />
                  </SoftBox>
                )}
                {canViewClient && (
                  <SoftBox sx={listSearchControlSx}>
                    <SoftSelect
                      size="small"
                      compactMenu
                      placeholder="Client"
                      value={clientFilter}
                      options={clientFilterOptions}
                      isLoading={projectClientFilterOptionsLoading}
                      onChange={setClientFilter}
                      isSearchable
                      isClearable
                      isMulti
                      sx={listSearchSelectSx}
                    />
                  </SoftBox>
                )}
              </SoftBox>
            )}
          </SoftBox>

          {loading ? (
            <SoftBox display="flex" justifyContent="center" alignItems="center" height="260px">
              <FlagLoader />
              <SoftTypography variant="body2" color="text" ml={1}>
                Loading country metrics...
              </SoftTypography>
            </SoftBox>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "none",
                overflowX: "hidden",
                width: "100%",
                borderTop: "1px solid #edf2f7",
              }}
            >
              <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    {canViewCountry && <TableCell rowSpan={2} sx={tableHeaderCellSx} width="7%">
                      <ProjectTableSortLabel label="Country" sortKey="country" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewCountryProjectName && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="6%">
                      <ProjectTableSortLabel label="Country Project Name" sortKey="country_project_name" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewRequiredN && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="4%">
                      <ProjectTableSortLabel label="Required N" sortKey="required_n" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewCountryQuota && (
                      <TableCell rowSpan={2} sx={metricHeaderCellSx} width="5%">
                        <ProjectTableSortLabel label="Country Clicks Quota (Cap)" sortKey="country_clicks_quota" sortConfig={sortConfig} onSort={handleSort} />
                      </TableCell>
                    )}
                    {canViewIr && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3%">
                      <ProjectTableSortLabel label="IR(%)" sortKey="ir" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewLoi && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3%">
                      <ProjectTableSortLabel label="LOI(mins)" sortKey="loi" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewCpi && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3.75%">
                      <ProjectTableSortLabel label="CPI" sortKey="cpi" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewCompleteDeviceBrowser && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="7%">
                      <ProjectTableSortLabel label="Device/Browser" sortKey="device_browser" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewStats && <TableCell
                      colSpan={5}
                      sx={{ ...metricHeaderCellSx, backgroundColor: "#fde047", color: "#1e293b" }}
                    >
                      Stats
                    </TableCell>}
                    {canViewInternalUserMetrics && <TableCell
                      colSpan={5}
                      sx={{ ...metricHeaderCellSx, backgroundColor: "#fde047", color: "#1e293b" }}
                    >
                      Live
                    </TableCell>}
                    {canViewRtr && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3.75%">
                      <ProjectTableSortLabel label="V.M" sortKey="vm" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {showEpc && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3.75%">
                      <ProjectTableSortLabel label="EPC" sortKey="epc" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewRevenue && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="5%">
                      <ProjectTableSortLabel label="Revenue" sortKey="revenue" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {showCountryUserUrlColumn && (
                      <TableCell
                        rowSpan={2}
                        sx={{ ...metricHeaderCellSx, whiteSpace: "nowrap", overflowWrap: "normal" }}
                        width="4.75%"
                      >
                        <ProjectTableSortLabel label="User Url" sortKey="links" sortConfig={sortConfig} onSort={handleSort} />
                      </TableCell>
                    )}
                    {canViewStatus && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="5%">
                      <ProjectTableSortLabel label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {showCountryRowActions && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="6.5%">
                      <SoftBox display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                        {[
                          canViewComments ? "comment" : null,
                          projectPermissions.canAction("mail") ? "mail" : null,
                          projectPermissions.canAction("chat") ? "chat" : null,
                        ].filter(Boolean).map((icon) => (
                          <Icon key={icon} sx={{ fontSize: "0.85rem !important" }}>
                            {icon}
                          </Icon>
                        ))}
                      </SoftBox>
                    </TableCell>}
                    {showCountryStatusActions && <TableCell
                      rowSpan={2}
                      sx={{ ...metricHeaderCellSx, px: 0, whiteSpace: "nowrap", overflowWrap: "normal" }}
                      width="1.75%"
                    >
                      <Icon sx={{ fontSize: "0.95rem !important" }}>more_vert</Icon>
                    </TableCell>}
                  </TableRow>
                  <TableRow>
                    {canViewStats && [
                      { label: "c/QS", key: "complete", color: "#0ea5e9" },
                      { label: "T", key: "terminate", color: "#ef4444" },
                      { label: "OQ", key: "quota_full", color: "#f59e0b" },
                      { label: "ST", key: "security_terminate", color: "#ef4444" },
                      { label: "P", key: "initiate", color: "#334155" },
                    ].map((column) => (
                      <TableCell
                        key={`stats-${column.label}`}
                        sx={{
                          ...metricHeaderCellSx,
                          color: column.color,
                          backgroundColor: "#f8fafc",
                        }}
                      >
                        <ProjectTableSortLabel label={column.label} sortKey={column.key} sortConfig={sortConfig} onSort={handleSort} />
                      </TableCell>
                    ))}
                    {canViewInternalUserMetrics && metricVendorColumns.map((vendor, index) => (
                      <TableCell
                        key={`vendor-${vendor.id}-${index}`}
                        title={vendor.name}
                        sx={{
                          ...metricHeaderCellSx,
                          color: "#0f172a",
                          backgroundColor: index % 2 === 0 ? "#e2e8f0" : "#fee2e2",
                        }}
                      >
                        <ProjectTableSortLabel label={vendor.initial} sortKey={`internal_vendor:${vendor.id}`} sortConfig={sortConfig} onSort={handleSort} />
                      </TableCell>
                    ))}
                    {canViewInternalUserMetrics && (
                    <TableCell
                      sx={{
                        ...metricHeaderCellSx,
                        color: "#0284c7",
                        backgroundColor: "#e0f2fe",
                      }}
                    >
                      <ProjectTableSortLabel label="O" sortKey="external_vendors" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCountries.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={Math.max(countryColumnCount, 1)}
                        sx={tableBodyCellSx}
                        align="center"
                      >
                        No countries found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedCountries.map((country) => {
                      const metrics = country.project_metrics || {};
                      const stats = metrics.stats || {};
                      const vendors = metrics.vendors || { count: 0, breakdown: emptyBreakdown };

                      return (
                        <React.Fragment key={country.id}>
                          <TableRow hover sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                            {canViewCountry && <TableCell sx={tableBodyCellSx}>
                              <SoftBox
                                component="button"
                                type="button"
                                disabled={Boolean(country.is_historical_link)}
                                onClick={() => {
                                  if (!country.is_historical_link) openCountryVendors(country);
                                }}
                                sx={{
                                  display: "block",
                                  p: 0,
                                  color: "#0ea5e9",
                                  background: "transparent",
                                  border: 0,
                                  fontFamily: "inherit",
                                  fontSize: "10px",
                                  fontWeight: 400,
                                  lineHeight: 1.1,
                                  textAlign: "left",
                                  cursor: country.is_historical_link ? "default" : "pointer",
                                  opacity: 1,
                                  "&:hover": {
                                    textDecoration: country.is_historical_link ? "none" : "underline",
                                  },
                                }}
                              >
                                {textOrDash(country.country_name)}
                                {country.is_historical_link ? " (Old Link)" : ""}
                              </SoftBox>
                              <SoftTypography
                                variant="caption"
                                color="text"
                                display="block"
                                sx={{ fontSize: "10px", fontWeight: 400, lineHeight: 1.1 }}
                              >
                                {textOrDash(country.country_code)}
                              </SoftTypography>
                            </TableCell>}
                            {canViewCountryProjectName && <TableCell
                              sx={{
                                ...metricBodyCellSx,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={country.country_project_name || ""}
                            >
                              {textOrDash(country.country_project_name)}
                            </TableCell>}
                            {canViewRequiredN && <TableCell sx={metricBodyCellSx}>
                              {textOrDash(country.required_n)}
                            </TableCell>}
                            {canViewCountryQuota && <TableCell sx={metricBodyCellSx}>
                              {textOrDash(country.country_clicks_quota)}
                            </TableCell>}
                            {canViewIr && <TableCell sx={metricBodyCellSx}>
                              <IrMetric average={metrics.average_ir} configured={country.ir} />
                            </TableCell>}
                            {canViewLoi && <TableCell sx={metricBodyCellSx}>
                              <LoiMetric
                                average={country.project_metrics?.average_loi}
                                configured={country.loi}
                              />
                            </TableCell>}
                            {canViewCpi && <TableCell sx={metricBodyCellSx}>
                              {country.won_cpi === null || country.won_cpi === undefined
                                ? "-"
                                : `$${country.won_cpi}`}
                            </TableCell>}
                            {canViewCompleteDeviceBrowser && (
                              <TableCell sx={{ ...metricBodyCellSx, minWidth: 0, px: 0.05 }}>
                                <CompleteDeviceMetric
                                  metric={metrics.complete_device_metrics}
                                  total={stats.complete}
                                />
                              </TableCell>
                            )}
                            {canViewStats && (
                              <>
                                <TableCell sx={{ ...metricBodyCellSx, fontWeight: 700 }}>
                                  <StatsHoverTooltip
                                    label={String(country.country_name || country.name || "Country")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span" sx={{ display: "inline-flex" }}>
                                      <SoftBox component="span" sx={{ color: Number(stats.complete || 0) > 0 ? "#16a34a" : "#dc2626" }}>
                                        {Number(stats.complete || 0)}
                                      </SoftBox>
                                      <SoftBox component="span" sx={{ color: "#0ea5e9" }}>
                                        /{quotaText(country.country_quota)}
                                      </SoftBox>
                                    </SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                                <TableCell sx={{ ...metricBodyCellSx, color: "#dc2626", fontWeight: 700 }}>
                                  <StatsHoverTooltip
                                    label={String(country.country_name || country.name || "Country")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span">{Number(stats.terminate || 0)}</SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                                <TableCell sx={{ ...metricBodyCellSx, color: "#d97706", fontWeight: 700 }}>
                                  <StatsHoverTooltip
                                    label={String(country.country_name || country.name || "Country")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span">{Number(stats.quota_full || 0)}</SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                                <TableCell sx={{ ...metricBodyCellSx, color: "#dc2626", fontWeight: 700 }}>
                                  <StatsHoverTooltip
                                    label={String(country.country_name || country.name || "Country")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span">
                                      {Number(stats.security_terminate || 0)}
                                    </SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                                <TableCell sx={{ ...metricBodyCellSx, color: "#334155", fontWeight: 700 }}>
                                  <StatsHoverTooltip
                                    label={String(country.country_name || country.name || "Country")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span">{Number(stats.initiate || 0)}</SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                              </>
                            )}
                            {canViewInternalUserMetrics && metricVendorColumns.map((vendor, index) => {
                              const source = metricSourceForVendor(country, vendor);

                              return (
                                <TableCell
                                  key={`${country.id}-${vendor.id}-${index}`}
                                  sx={{
                                    ...metricBodyCellSx,
                                    backgroundColor: index % 2 === 0 ? "#f1f5f9" : "#fff1f2",
                                  }}
                                >
                                  <Tooltip
                                    placement="bottom"
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          p: 0,
                                          maxWidth: "none",
                                          backgroundColor: "transparent",
                                          boxShadow: "none",
                                        },
                                      },
                                    }}
                                    title={
                                      <MetricHoverCard
                                        label={vendor.name}
                                        breakdown={source.breakdown || emptyBreakdown}
                                      />
                                    }
                                  >
                                    <SoftBox
                                      component="span"
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "100%",
                                        minHeight: 17,
                                        cursor: "help",
                                      }}
                                    >
                                      {Number(source.count || 0)}
                                    </SoftBox>
                                  </Tooltip>
                                </TableCell>
                              );
                            })}
                            {canViewInternalUserMetrics && (
                            <TableCell
                              sx={{
                                ...metricBodyCellSx,
                                color: "#0284c7",
                                backgroundColor: "#e0f2fe",
                              }}
                            >
                              <Tooltip
                                placement="bottom"
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      p: 0,
                                      maxWidth: "none",
                                      backgroundColor: "transparent",
                                      boxShadow: "none",
                                    },
                                  },
                                }}
                                title={
                                  <MetricHoverCard
                                    label="External Vendors"
                                    breakdown={vendors.breakdown || emptyBreakdown}
                                  />
                                }
                              >
                                <SoftBox
                                  component="span"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100%",
                                    minHeight: 17,
                                    cursor: "help",
                                  }}
                                >
                                  {Number(vendors.count || 0)}
                                </SoftBox>
                              </Tooltip>
                            </TableCell>
                            )}
                            {canViewRtr && <TableCell sx={{ ...metricBodyCellSx, backgroundColor: "#fee2e2" }}>
                              {percentageText(metrics.rtr)}
                            </TableCell>}
                            {showEpc && <TableCell sx={{ ...metricBodyCellSx, backgroundColor: "#e0f2fe" }}>
                              {epcText(metrics.revenue, metrics.overall_breakdown, stats)}
                            </TableCell>}
                            {canViewRevenue && <TableCell sx={{ ...metricBodyCellSx, color: "#15803d" }}>
                              {currencyText(metrics.revenue)}
                            </TableCell>}
                            {showCountryUserUrlColumn && (
                              <TableCell sx={metricBodyCellSx}>
                                <Tooltip
                                  title={
                                    country.internal_live_url || country.internal_test_url
                                      ? "Show internal live and test Url"
                                      : "Internal user links not available"
                                  }
                                >
                                  <span>
                                    <SoftButton
                                      variant="outlined"
                                      color="info"
                                      size="small"
                                      disabled={!country.internal_live_url && !country.internal_test_url}
                                      onClick={() => openCountryLinksDialog(country)}
                                      sx={{
                                        minWidth: 0,
                                        height: 20,
                                        minHeight: "20px !important",
                                        px: 0.45,
                                        py: 0,
                                        fontSize: "9px",
                                        lineHeight: 1,
                                        whiteSpace: "nowrap",
                                        textTransform: "none",
                                      }}
                                    >
                                      User Url
                                    </SoftButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            )}
                            {canViewStatus && <TableCell sx={metricBodyCellSx}>{countryStatusBadge(country)}</TableCell>}
                            {showCountryRowActions && <TableCell sx={metricBodyCellSx}>
                              {country.is_historical_link ? "-" : <SoftBox display="flex" justifyContent="center" alignItems="center" gap={0.15}>
                                {canViewComments && <Tooltip title={country.comments || "No comments"}>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    sx={{ p: 0.2 }}
                                    onClick={() => openCountryCommunicationDialog("comment", country)}
                                  >
                                    <Icon sx={{ fontSize: "0.85rem !important" }}>comment</Icon>
                                  </IconButton>
                                </Tooltip>}
                                {projectPermissions.canAction("mail") && <Tooltip title="Mail">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    sx={{ p: 0.2 }}
                                    onClick={() => openCountryCommunicationDialog("mail", country)}
                                  >
                                    <Icon sx={{ fontSize: "0.85rem !important" }}>mail</Icon>
                                  </IconButton>
                                </Tooltip>}
                                {canUseProjectLiveChat && <Tooltip title="Project live chat">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    sx={{ p: 0.2 }}
                                    onClick={() =>
                                      openProjectLiveChat({
                                        id: bid?.id || id,
                                        quest_code: bid?.quest_code || bid?.project_code || id,
                                        project_title:
                                          bid?.project_title || bid?.project_name || bid?.quest_code || "",
                                      })
                                    }
                                  >
                                    <ProjectLiveChatBadge projectId={bid?.id || id}>
                                      <Icon sx={{ fontSize: "0.85rem !important" }}>forum</Icon>
                                    </ProjectLiveChatBadge>
                                  </IconButton>
                                </Tooltip>}
                                {canUseActions &&
                                  canShowLiveCountryActions &&
                                  canOpenCountryDetails &&
                                  /^\d+$/.test(String(country.id || "")) && (
                                  <Tooltip title="Edit country details">
                                    <IconButton
                                      size="small"
                                      onClick={() => openCountryQuotaDialog(country)}
                                      sx={{ p: 0.2, color: "#344767" }}
                                    >
                                      <Icon sx={{ fontSize: "0.85rem !important" }}>edit</Icon>
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </SoftBox>}
                            </TableCell>}
                            {showCountryStatusActions && <TableCell sx={{ ...metricBodyCellSx, px: 0 }}>
                              {canUseActions && allowedCountryStatusActions.length > 0 && /^\d+$/.test(String(country.id || "")) ? (
                                <Tooltip title="Country status actions">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={statusLoading}
                                      onClick={(event) => openCountryStatusMenu(event, country)}
                                      sx={{ p: 0.25 }}
                                    >
                                      <Icon sx={{ fontSize: "0.95rem !important" }}>more_vert</Icon>
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : (
                                "-"
                              )}
                            </TableCell>}
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        <Dialog
          open={linkDialogOpen}
          onClose={closeCountryLinksDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: "8px" } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: "18px",
              py: 1.25,
            }}
          >
            Internal User Url
            <IconButton size="small" onClick={closeCountryLinksDialog}>
              <Icon>close</Icon>
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 0.5, pb: 1 }}>
            <SoftBox display="grid" gap={1.25}>
              {canViewCountry && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Country
                  </SoftTypography>
                  <SoftTypography variant="body2" color="text" sx={{ fontSize: "12px" }}>
                    {textOrDash(linkDialogCountry?.country_name)} /{" "}
                    {textOrDash(linkDialogCountry?.country_code)}
                  </SoftTypography>
                </SoftBox>
              )}
              {canViewLiveUrl && (
                <SoftBox gridColumn={{ xs: "1", md: "1 / -1" }}>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Live Url
                  </SoftTypography>
                  <SoftInput
                    multiline
                    rows={2}
                    value={linkDialogCountry?.internal_live_url || ""}
                    inputProps={{ readOnly: true }}
                    sx={{
                      "& textarea": {
                        fontSize: "13px",
                        lineHeight: 1.35,
                      },
                    }}
                  />
                  {canCopyUserUrl && (
                    <SoftButton
                      variant="gradient"
                      color="info"
                      size="small"
                      onClick={() => copyLink(linkDialogCountry?.internal_live_url, "Internal live")}
                      sx={{ mt: 0.75, px: 1.25 }}
                    >
                      <Icon sx={{ fontSize: "1rem !important", mr: 0.35 }}>content_copy</Icon>
                      Copy
                    </SoftButton>
                  )}
                </SoftBox>
              )}
              {canViewTestUrl && (
                <SoftBox gridColumn={{ xs: "1", md: "1 / -1" }}>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Test Url
                  </SoftTypography>
                  <SoftInput
                    multiline
                    rows={2}
                    value={linkDialogCountry?.internal_test_url || ""}
                    inputProps={{ readOnly: true }}
                    sx={{
                      "& textarea": {
                        fontSize: "13px",
                        lineHeight: 1.35,
                      },
                    }}
                  />
                  {canCopyUserUrl && (
                    <SoftButton
                      variant="gradient"
                      color="secondary"
                      size="small"
                      onClick={() => copyLink(linkDialogCountry?.internal_test_url, "Internal test")}
                      sx={{ mt: 0.75, px: 1.25 }}
                    >
                      <Icon sx={{ fontSize: "1rem !important", mr: 0.35 }}>content_copy</Icon>
                      Copy
                    </SoftButton>
                  )}
                </SoftBox>
              )}
            </SoftBox>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 1.5, pt: 0 }}>
            <SoftButton variant="outlined" color="dark" size="small" onClick={closeCountryLinksDialog}>
              Close
            </SoftButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={vendorDialogOpen}
          onClose={closeVendorDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: "8px" } }}
        >
          <DialogTitle sx={{ fontSize: "18px", py: 1.25 }}>
            Add Vendor
          </DialogTitle>
          <DialogContent sx={{ pt: 0.5, pb: 1 }}>
            <SoftBox display="grid" gap={1.25}>
              <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1}>
                {canViewProjectCode && (
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold" color="dark">
                      Project Code
                    </SoftTypography>
                    <SoftTypography variant="body2" color="text" sx={{ fontSize: "12px" }}>
                      {textOrDash(bid?.quest_code)}
                    </SoftTypography>
                  </SoftBox>
                )}
                {canViewCountry && (
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold" color="dark">
                      Countries
                    </SoftTypography>
                    <SoftTypography variant="body2" color="text" sx={{ fontSize: "12px" }}>
                      All project countries
                    </SoftTypography>
                  </SoftBox>
                )}
              </SoftBox>

              {vendorForms.map((form, index) => (
                <SoftBox
                  key={`vendor-form-${index + 1}`}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    p: 1,
                    display: "grid",
                    gap: 1,
                  }}
                >
                  <SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                    <SoftTypography variant="caption" fontWeight="bold" color="dark">
                      Vendor {index + 1}
                    </SoftTypography>
                    {vendorForms.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeVendorFormRow(index)}
                        sx={{ p: 0.25, color: "#ef4444" }}
                      >
                        <Icon sx={{ fontSize: "0.95rem !important" }}>delete</Icon>
                      </IconButton>
                    )}
                  </SoftBox>

                  <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }} gap={1}>
                  {canViewVendors && (
                    <SoftBox gridColumn={{ xs: "1", md: "1 / -1" }}>
                      <SoftTypography variant="caption" fontWeight="bold" color="dark">
                        Vendor
                      </SoftTypography>
                      <SoftSelect
                        compact
                        size="small"
                        placeholder="Select Vendor"
                        value={form.vendor}
                        options={vendorOptions}
                        isLoading={vendorOptionsLoading}
                        onMenuOpen={fetchVendorOptions}
                        onChange={(value) => updateVendorForm(index, "vendor", value)}
                        isSearchable
                        isClearable
                      />
                    </SoftBox>
                  )}
                    {canViewVendorQuota && (
                      <SoftBox>
                        <SoftTypography variant="caption" fontWeight="bold" color="dark">
                          Vendor Completes Quota (Cap)
                        </SoftTypography>
                        <SoftInput
                          size="small"
                          value={form.vendor_quota}
                          placeholder="Vendor Completes Quota (Cap)"
                          onChange={(event) => updateVendorForm(index, "vendor_quota", event.target.value)}
                        />
                      </SoftBox>
                    )}
                    {canViewVendorQuota && (
                      <SoftBox>
                        <SoftTypography variant="caption" fontWeight="bold" color="dark">
                          Vendor Clicks Quota (Cap)
                        </SoftTypography>
                        <SoftInput
                          size="small"
                          value={form.vendor_clicks_quota}
                          placeholder="Vendor Clicks Quota (Cap)"
                          onChange={(event) =>
                            updateVendorForm(index, "vendor_clicks_quota", event.target.value)
                          }
                        />
                      </SoftBox>
                    )}
                    {canViewCpi && (
                      <SoftBox>
                        <SoftTypography variant="caption" fontWeight="bold" color="dark">
                          Target CPI
                        </SoftTypography>
                        <SoftInput
                          size="small"
                          value={form.target_cpi}
                          placeholder="Target CPI"
                          onChange={(event) => updateVendorForm(index, "target_cpi", event.target.value)}
                        />
                      </SoftBox>
                    )}
                    <SoftBox display="flex" flexWrap="wrap" gap={2} gridColumn={{ xs: "1", md: "1 / -1" }}>
                      <FormControlLabel
                        sx={{ m: 0, alignItems: "center", gap: 0.5 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={Boolean(form.is_verisoul_security)}
                            onChange={(event) =>
                              updateVendorForm(index, "is_verisoul_security", event.target.checked)
                            }
                            sx={{
                              p: 0,
                              color: "#d2d6da",
                              "&.Mui-checked": { color: "#17c1e8" },
                            }}
                          />
                        }
                        label={
                          <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: "#17c1e8" }}
                          >
                            Enable Verisoul Security
                          </SoftTypography>
                        }
                      />
                      <FormControlLabel
                        sx={{ m: 0, alignItems: "center", gap: 0.5 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={Boolean(form.is_surveyarmor_security)}
                            onChange={(event) =>
                              updateVendorForm(index, "is_surveyarmor_security", event.target.checked)
                            }
                            sx={{
                              p: 0,
                              color: "#d2d6da",
                              "&.Mui-checked": { color: "#17c1e8" },
                            }}
                          />
                        }
                        label={
                          <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: "#17c1e8" }}
                          >
                            Enable SurveyArmor Security
                          </SoftTypography>
                        }
                      />
                    </SoftBox>
                  </SoftBox>

                  {canViewVendorRedirectDetails && (
                    <>
                      <SoftTypography variant="caption" fontWeight="bold" color="dark">
                        Redirect Details
                      </SoftTypography>
                      <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }} gap={1}>
                        {vendorRedirectFields.map((field) => (
                          <SoftBox key={field.key}>
                            <SoftTypography variant="caption" fontWeight="bold" color="dark">
                              {field.label}
                            </SoftTypography>
                            <SoftInput
                              size="small"
                              value={form[field.key]}
                              placeholder="https://vendor.com/survey?status=1&rid=[identifier]"
                              onChange={(event) => updateVendorForm(index, field.key, event.target.value)}
                            />
                          </SoftBox>
                        ))}
                      </SoftBox>
                    </>
                  )}
                </SoftBox>
              ))}
              {canSubmitAddVendor && (
              <SoftBox display="flex" justifyContent="flex-end">
                <SoftButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={addVendorFormRow}
                  sx={{ px: 1.25, py: 0.45, minHeight: 28, fontSize: "11px", textTransform: "none" }}
                >
                  Add More
                </SoftButton>
              </SoftBox>
              )}
            </SoftBox>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 1.5, pt: 0 }}>
            <SoftButton variant="outlined" color="dark" size="small" onClick={closeVendorDialog}>
              Close
            </SoftButton>
            {canSubmitAddVendor && (
              <SoftButton
                variant="gradient"
                color="info"
                size="small"
                disabled={vendorSubmitLoading}
                onClick={submitProjectVendor}
              >
                {vendorSubmitLoading ? "Saving..." : "Save"}
              </SoftButton>
            )}
          </DialogActions>
        </Dialog>

        <Dialog
          open={countryDialogOpen}
          onClose={closeCountryQuotaDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: "8px" } }}
        >
          <DialogTitle sx={{ fontSize: "18px", py: 1.25 }}>
            {countryDialogMode === "add" ? "Add Country Links" : "Edit Country Details"}
          </DialogTitle>
          <DialogContent sx={{ pt: 0.5, pb: 1 }}>
            <SoftBox
              display="grid"
              gridTemplateColumns={{ xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }}
              gap={1}
            >
              {canViewProjectCode && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Project Code
                  </SoftTypography>
                  <SoftTypography variant="body2" color="text" sx={{ fontSize: "12px" }}>
                    {textOrDash(bid?.quest_code)}
                  </SoftTypography>
                </SoftBox>
              )}
              {canViewCountry && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Country
                  </SoftTypography>
                  {countryDialogMode === "add" ? (
                    <SoftSelect
                      compact
                      size="small"
                      placeholder="Select Country"
                      value={countryForm.country}
                      options={countryOptions}
                      isLoading={countryOptionsLoading}
                      onMenuOpen={fetchCountryOptions}
                      onChange={(value) => updateCountryForm("country", value)}
                      isSearchable
                      isClearable
                      formatOptionLabel={formatCountryOptionLabel}
                    />
                  ) : (
                    <SoftTypography variant="body2" color="text" sx={{ fontSize: "12px" }}>
                      {textOrDash(countryDialogCountry?.country_name)}
                    </SoftTypography>
                  )}
                </SoftBox>
              )}
              {canViewCountry && (
                <SoftBox>
                  <SoftTypography variant='caption' fontWeight='bold' color='dark'>
                    States (Optional)
                  </SoftTypography>
                  <SoftSelect
                    compact
                    size='small'
                    placeholder='All states in selected country'
                    value={countryForm.states}
                    options={countryStateOptions}
                    isLoading={countryStateOptionsLoading}
                    isDisabled={!selectOptionValue(countryForm.country)}
                    onMenuOpen={() =>
                      fetchCountryStateOptions(selectOptionValue(countryForm.country))
                    }
                    onChange={(value) => updateCountryForm('states', value || [])}
                    isMulti
                    closeMenuOnSelect={false}
                    isSearchable
                    isClearable
                  />
                  <SoftTypography variant='caption' color='text'>
                    Leave blank to allow every state in this country.
                  </SoftTypography>
                </SoftBox>
              )}
              {canViewRequiredN && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Required N
                  </SoftTypography>
                  <SoftInput
                    size="small"
                    value={countryForm.required_n}
                    placeholder="Required N"
                    onChange={(event) => updateCountryForm("required_n", event.target.value)}
                  />
                </SoftBox>
              )}
              {canViewCountryProjectName && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Country Project Name
                  </SoftTypography>
                  <SoftInput
                    size="small"
                    value={countryForm.country_project_name}
                    placeholder="Country Project Name"
                    onChange={(event) => updateCountryForm("country_project_name", event.target.value)}
                  />
                </SoftBox>
              )}
              {canViewCountryQuota && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Country Completes Quota (Cap)
                  </SoftTypography>
                  <SoftInput
                    size="small"
                    value={countryForm.country_quota}
                    placeholder="Country Completes Quota (Cap)"
                    onChange={(event) => updateCountryForm("country_quota", event.target.value)}
                  />
                </SoftBox>
              )}
              {canViewCountryQuota && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Country Clicks Quota (Cap)
                  </SoftTypography>
                  <SoftInput
                    size="small"
                    value={countryForm.country_clicks_quota}
                    placeholder="Country Clicks Quota (Cap)"
                    onChange={(event) =>
                      updateCountryForm("country_clicks_quota", event.target.value)
                    }
                  />
                </SoftBox>
              )}
              {canViewIr && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    IR(%)
                  </SoftTypography>
                  <SoftInput
                    size="small"
                    value={countryForm.ir}
                    placeholder="IR(%)"
                    onChange={(event) => updateCountryForm("ir", event.target.value)}
                  />
                </SoftBox>
              )}
              {canViewLoi && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    LOI(mins)
                  </SoftTypography>
                  <SoftInput
                    size="small"
                    value={countryForm.loi}
                    placeholder="LOI(mins)"
                    onChange={(event) => updateCountryForm("loi", event.target.value)}
                  />
                </SoftBox>
              )}
              {canViewCpi && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    CPI
                  </SoftTypography>
                  <SoftInput
                    size="small"
                    value={countryForm.won_cpi}
                    placeholder="CPI"
                    onChange={(event) => updateCountryForm("won_cpi", event.target.value)}
                  />
                </SoftBox>
              )}
              {canViewLiveUrl && (
                <SoftBox gridColumn={{ xs: "1", md: "1 / -1" }}>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Live Url
                  </SoftTypography>
                  <SoftInput
                    size="small"
                    value={countryForm.live_url}
                    placeholder="Enter live Url"
                    onChange={(event) => updateCountryForm("live_url", event.target.value)}
                  />
                </SoftBox>
              )}
              {canViewTestUrl && (
                <SoftBox gridColumn={{ xs: "1", md: "1 / -1" }}>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Test Url
                  </SoftTypography>
                  <SoftInput
                    size="small"
                    value={countryForm.test_url}
                    placeholder="Enter test Url"
                    onChange={(event) => updateCountryForm("test_url", event.target.value)}
                  />
                </SoftBox>
              )}
              {(canViewSecure || canViewCaptchaSecurity) && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Security
                  </SoftTypography>
                  <SoftBox display="flex" gap={1.25} alignItems="center" minHeight={30}>
                    {canViewSecure && (
                      <SoftBox component="label" display="flex" alignItems="center" gap={0.4}>
                        <input
                          type="checkbox"
                          checked={Boolean(countryForm.is_secure)}
                          onChange={(event) => updateCountryForm("is_secure", event.target.checked)}
                        />
                        <SoftTypography variant="caption" color="text">
                          Secure
                        </SoftTypography>
                      </SoftBox>
                    )}
                    {canViewCaptchaSecurity && (
                      <SoftBox component="label" display="flex" alignItems="center" gap={0.4}>
                        <input
                          type="checkbox"
                          checked={Boolean(countryForm.is_captcha_security)}
                          onChange={(event) => updateCountryForm("is_captcha_security", event.target.checked)}
                        />
                        <SoftTypography variant="caption" color="text">
                          Captcha
                        </SoftTypography>
                      </SoftBox>
                    )}
                  </SoftBox>
                </SoftBox>
              )}
              {canManageCountryLinkHitGap && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Country Link Hit Gap (Seconds)
                  </SoftTypography>
                  <SoftInput
                    size="small"
                    type="number"
                    value={countryForm.link_hit_gap_seconds}
                    placeholder="NULL"
                    inputProps={{ min: 1, max: 86400, step: 1 }}
                    onChange={(event) =>
                      updateCountryForm("link_hit_gap_seconds", event.target.value)
                    }
                  />
                </SoftBox>
              )}
              <SoftBox display="flex" flexWrap="wrap" gap={2} gridColumn={{ xs: "1", md: "1 / -1" }}>
                <FormControlLabel
                  sx={{ m: 0, alignItems: "center", gap: 0.5 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(countryForm.is_verisoul_security)}
                      onChange={(event) =>
                        updateCountryForm("is_verisoul_security", event.target.checked)
                      }
                      sx={{
                        p: 0,
                        color: "#d2d6da",
                        "&.Mui-checked": { color: "#17c1e8" },
                      }}
                    />
                  }
                  label={
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#17c1e8" }}>
                      Enable Verisoul Security
                    </SoftTypography>
                  }
                />
                <FormControlLabel
                  sx={{ m: 0, alignItems: "center", gap: 0.5 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(countryForm.is_surveyarmor_security)}
                      onChange={(event) =>
                        updateCountryForm("is_surveyarmor_security", event.target.checked)
                      }
                      sx={{
                        p: 0,
                        color: "#d2d6da",
                        "&.Mui-checked": { color: "#17c1e8" },
                      }}
                    />
                  }
                  label={
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#17c1e8" }}>
                      Enable SurveyArmor Security
                    </SoftTypography>
                  }
                />
              </SoftBox>
              {canViewDeviceType && (
                <SoftBox gridColumn={{ xs: "1", md: "1 / -1" }}>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Device Type
                  </SoftTypography>
                  <SoftBox display="flex" flexWrap="wrap" gap={1.25} mt={0.25}>
                    {deviceTypeOptions.map((option) => (
                      <SoftBox
                        key={option.value}
                        component="label"
                        display="flex"
                        alignItems="center"
                        gap={0.4}
                      >
                        <input
                          type="checkbox"
                          checked={(countryForm.device_type || []).includes(option.value)}
                          onChange={() => toggleDeviceType(option.value)}
                        />
                        <SoftTypography variant="caption" color="text">
                          {option.label}
                        </SoftTypography>
                      </SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
              )}
              {canViewTargeting && (
                <SoftBox gridColumn={{ xs: "1", md: "1 / -1" }}>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Targeting
                  </SoftTypography>
                  <SoftInput
                    size="small"
                    multiline
                    rows={2}
                    value={countryForm.targeting}
                    placeholder="Targeting"
                    onChange={(event) => updateCountryForm("targeting", event.target.value)}
                  />
                </SoftBox>
              )}
              {canViewComments && (
                <SoftBox gridColumn={{ xs: "1", md: "1 / -1" }}>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Comments
                  </SoftTypography>
                  <SoftInput
                    size="small"
                    multiline
                    rows={2}
                    value={countryForm.comments}
                    placeholder="Comments"
                    onChange={(event) => updateCountryForm("comments", event.target.value)}
                  />
                </SoftBox>
              )}
            </SoftBox>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 1.5, pt: 0 }}>
            <SoftButton variant="outlined" color="dark" size="small" onClick={closeCountryQuotaDialog}>
              Close
            </SoftButton>
            {canSaveCountryDialog && (
              <SoftButton
                variant="gradient"
                color="info"
                size="small"
                disabled={quotaSubmitLoading}
                onClick={submitCountryQuota}
              >
                {quotaSubmitLoading ? "Saving..." : "Save"}
              </SoftButton>
            )}
          </DialogActions>
        </Dialog>

        <Dialog
          open={finalIdsDialogOpen}
          onClose={closeFinalIdsDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "14px",
              overflow: "hidden",
              boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2,
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <SoftBox display="flex" alignItems="center" gap={1}>
              <SoftBox
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "10px",
                  display: "grid",
                  placeItems: "center",
                  color: "#fff",
                  background: "linear-gradient(135deg, #0ea5e9, #2563eb)",
                }}
              >
                <Icon>upload_file</Icon>
              </SoftBox>
              <SoftBox>
                <SoftTypography variant="h6" fontWeight="bold">
                  Upload Final IDs
                </SoftTypography>
                <SoftTypography variant="caption" color="text">
                  Preview first, then confirm the database update
                </SoftTypography>
              </SoftBox>
            </SoftBox>
            <IconButton
              size="small"
              onClick={closeFinalIdsDialog}
              disabled={finalIdsImportLoading || finalIdsConfirmLoading}
            >
              <Icon>close</Icon>
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 3, py: "20px !important" }}>
            <SoftBox display="grid" gap={2}>
              <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Final Status
                  </SoftTypography>
                  <SoftSelect
                    compact
                    size="small"
                    value={finalIdsStatus}
                    options={[
                      { value: "accepted", label: "Accepted" },
                      { value: "rejected", label: "Rejected" },
                    ]}
                    isSearchable={false}
                    onChange={(value) => {
                      setFinalIdsStatus(value);
                      resetFinalIdsProcessedState();
                    }}
                  />
              </SoftBox>

              <SoftBox
                display="flex"
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                flexDirection={{ xs: "column", sm: "row" }}
                gap={0.75}
                sx={{
                  border: "1px solid #dbeafe",
                  borderRadius: "10px",
                  backgroundColor: "#eff6ff",
                  px: 1.5,
                  py: 1,
                }}
              >
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Need the correct format?
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block">
                    Download the reusable sample with RID format and instructions.
                  </SoftTypography>
                </SoftBox>
                <SoftButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={downloadFinalIdsSample}
                  sx={{ whiteSpace: "nowrap", textTransform: "none" }}
                >
                  <Icon sx={{ mr: 0.5 }}>download</Icon>
                  Download Sample Excel
                </SoftButton>
              </SoftBox>

              <SoftBox
                component="label"
                sx={{
                  minHeight: 142,
                  border: `2px dashed ${finalIdsFile ? "#0ea5e9" : "#cbd5e1"}`,
                  borderRadius: "12px",
                  backgroundColor: finalIdsFile ? "#f0f9ff" : "#f8fafc",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  px: 2,
                  py: 2,
                  cursor: "pointer",
                  transition: "all 160ms ease",
                  "&:hover": {
                    borderColor: "#0ea5e9",
                    backgroundColor: "#f0f9ff",
                  },
                }}
              >
                <input
                  hidden
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onClick={(event) => {
                    event.currentTarget.value = "";
                  }}
                  onChange={(event) => {
                    setFinalIdsFile(event.target.files?.[0] || null);
                    resetFinalIdsProcessedState();
                  }}
                />
                <Icon sx={{ color: "#0ea5e9", fontSize: "2rem !important" }}>
                  cloud_upload
                </Icon>
                <SoftTypography variant="button" fontWeight="bold" color="dark">
                  {finalIdsFile ? finalIdsFile.name : "Choose Excel file"}
                </SoftTypography>
                <SoftTypography variant="caption" color="text" textAlign="center">
                  XLSX, XLS or CSV up to 10 MB. The first column heading must be RID.
                </SoftTypography>
              </SoftBox>

              {finalIdsError && (
                <SoftBox
                  sx={{
                    border: "1px solid #fecaca",
                    borderRadius: "10px",
                    backgroundColor: "#fef2f2",
                    color: "#b91c1c",
                    px: 1.5,
                    py: 1,
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  {finalIdsError}
                </SoftBox>
              )}

              {!finalIdsResult && (
                <SoftBox display="flex" justifyContent="flex-end">
                  <SoftButton
                    variant="gradient"
                    color="info"
                    disabled={
                      !bid?.id ||
                      !finalIdsStatus ||
                      !finalIdsFile ||
                      finalIdsImportLoading ||
                      finalIdsConfirmLoading
                    }
                    onClick={importFinalIdsPreview}
                  >
                    {finalIdsImportLoading ? (
                      <>
                        <CircularProgress size={16} color="inherit" sx={{ mr: 0.75 }} />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Icon sx={{ mr: 0.5 }}>preview</Icon>
                        Import & Preview
                      </>
                    )}
                  </SoftButton>
                </SoftBox>
              )}

              {finalIdsPreview && (
                <SoftBox
                  sx={{
                    border: "1px solid #bae6fd",
                    borderRadius: "12px",
                    backgroundColor: "#f8fdff",
                    p: 2,
                  }}
                >
                  <SoftBox
                    display="flex"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={0.75}
                    mb={1.5}
                  >
                    <SoftBox>
                      <SoftTypography variant="button" fontWeight="bold" color="dark">
                        Study Status Preview — {finalIdsPreview.project?.project_code}
                      </SoftTypography>
                      <SoftTypography variant="caption" color="text" display="block">
                        {finalIdsPreview.uploaded_rid_count} unique RID(s) loaded
                      </SoftTypography>
                    </SoftBox>
                    <SoftBox
                      sx={{
                        px: 1,
                        py: 0.45,
                        borderRadius: "999px",
                        color: "#0369a1",
                        backgroundColor: "#e0f2fe",
                        fontSize: "11px",
                        fontWeight: 700,
                      }}
                    >
                      Database not updated
                    </SoftBox>
                  </SoftBox>

                  <SoftBox
                    display="grid"
                    gridTemplateColumns={{
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(3, minmax(0, 1fr))",
                    }}
                    gap={1}
                  >
                    {[
                      ["Completed", "completed", "#16a34a"],
                      ["Terminate", "terminate", "#dc2626"],
                      ["Security Terminate", "security_terminate", "#be123c"],
                      ["Quota Full", "quota_full", "#d97706"],
                      ["Partial", "partial", "#475569"],
                      ["Client Rejected", "client_rejected", "#9333ea"],
                      ["Total", "total", "#2563eb"],
                    ].map(([label, key, color]) => (
                      <SoftBox
                        key={key}
                        sx={{
                          border: "1px solid #e2e8f0",
                          borderRadius: "10px",
                          backgroundColor: "#fff",
                          px: 1.25,
                          py: 1,
                        }}
                      >
                        <SoftTypography variant="caption" color="text">
                          {label}
                        </SoftTypography>
                        <SoftTypography variant="h6" fontWeight="bold" sx={{ color }}>
                          {Number(finalIdsPreview.counts?.[key] || 0)}
                        </SoftTypography>
                      </SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
              )}

              {finalIdsResult && (
                <SoftBox
                  sx={{
                    border: "1px solid #bbf7d0",
                    borderRadius: "12px",
                    backgroundColor: "#f7fff9",
                    p: 2,
                  }}
                >
                  <SoftBox display="flex" alignItems="center" gap={0.75} mb={1.5}>
                    <Icon sx={{ color: "#16a34a" }}>check_circle</Icon>
                    <SoftBox>
                      <SoftTypography variant="button" fontWeight="bold" color="dark">
                        Upload Complete — {finalIdsResult.project?.project_code}
                      </SoftTypography>
                      <SoftTypography variant="caption" color="text" display="block">
                        {Number(finalIdsResult.summary?.processed_studies || 0)} studies processed
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>

                  <SoftBox
                    sx={{
                      border: `1px solid ${
                        Number(finalIdsResult.summary?.unmatched_rids || 0) > 0
                          ? "#fed7aa"
                          : "#bbf7d0"
                      }`,
                      borderRadius: "10px",
                      backgroundColor:
                        Number(finalIdsResult.summary?.unmatched_rids || 0) > 0
                          ? "#fff7ed"
                          : "#f0fdf4",
                      color:
                        Number(finalIdsResult.summary?.unmatched_rids || 0) > 0
                          ? "#c2410c"
                          : "#15803d",
                      px: 1.5,
                      py: 1,
                      mb: 1,
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {finalIdsResult.mismatch_message}
                  </SoftBox>

                  <SoftBox
                    display="grid"
                    gridTemplateColumns={{
                      xs: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(3, minmax(0, 1fr))",
                    }}
                    gap={1}
                    mb={1}
                  >
                    {[
                      ["Accepted", finalIdsResult.summary?.total_accepted, "#16a34a"],
                      ["Rejected", finalIdsResult.summary?.total_rejected, "#dc2626"],
                      [
                        "Original Status Kept",
                        finalIdsResult.summary?.total_preserved_original_status,
                        "#475569",
                      ],
                      ["Matched RIDs", finalIdsResult.summary?.matched_rids, "#0284c7"],
                      ["Unmatched RIDs", finalIdsResult.summary?.unmatched_rids, "#d97706"],
                    ].map(([label, value, color]) => (
                      <SoftBox
                        key={label}
                        sx={{
                          border: "1px solid #e2e8f0",
                          borderRadius: "10px",
                          backgroundColor: "#fff",
                          px: 1.25,
                          py: 1,
                        }}
                      >
                        <SoftTypography variant="caption" color="text">
                          {label}
                        </SoftTypography>
                        <SoftTypography variant="h6" fontWeight="bold" sx={{ color }}>
                          {Number(value || 0)}
                        </SoftTypography>
                      </SoftBox>
                    ))}
                  </SoftBox>

                  <SoftBox
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }}
                    gap={1}
                  >
                    {[
                      ["Total Revenue", finalIdsResult.summary?.total_revenue, "#2563eb"],
                      ["Loss Revenue", finalIdsResult.summary?.loss_revenue, "#dc2626"],
                      ["Profit Revenue", finalIdsResult.summary?.profit_revenue, "#16a34a"],
                      [
                        "Final Profit Revenue",
                        finalIdsResult.summary?.final_profit_revenue,
                        "#7c3aed",
                      ],
                    ].map(([label, value, color]) => (
                      <SoftBox
                        key={label}
                        sx={{
                          border: "1px solid #e2e8f0",
                          borderRadius: "10px",
                          backgroundColor: "#fff",
                          px: 1.25,
                          py: 1,
                        }}
                      >
                        <SoftTypography variant="caption" color="text">
                          {label}
                        </SoftTypography>
                        <SoftTypography variant="h6" fontWeight="bold" sx={{ color }}>
                          {currencyText(value)}
                        </SoftTypography>
                      </SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
              )}
            </SoftBox>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 1.75,
              borderTop: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
            }}
          >
            <SoftButton
              variant="outlined"
              color="dark"
              size="small"
              onClick={closeFinalIdsDialog}
              disabled={finalIdsImportLoading || finalIdsConfirmLoading}
            >
              {finalIdsResult ? "Done" : "Close"}
            </SoftButton>
            {finalIdsPreview && !finalIdsResult && (
              <SoftButton
                variant="gradient"
                color="success"
                size="small"
                onClick={confirmFinalIdsUpload}
                disabled={finalIdsConfirmLoading}
              >
                {finalIdsConfirmLoading ? (
                  <>
                    <CircularProgress size={16} color="inherit" sx={{ mr: 0.75 }} />
                    Confirming...
                  </>
                ) : (
                  <>
                    <Icon sx={{ mr: 0.5 }}>verified</Icon>
                    Confirm Upload Final IDs
                  </>
                )}
              </SoftButton>
            )}
          </DialogActions>
        </Dialog>

        <ProjectCommunicationComposerDialog
          open={Boolean(communicationDialog)}
          type={communicationDialog?.type}
          scope={communicationDialog?.scope}
          onClose={() => setCommunicationDialog(null)}
          onSent={fetchCountryMetrics}
          onNotify={showMessage}
        />

        <Menu
          anchorEl={actionsAnchor}
          open={Boolean(actionsAnchor)}
          onClose={() => {
            if (!statusLoading) closeCountryStatusMenu();
          }}
          PaperProps={{
            sx: {
              minWidth: 190,
              borderRadius: "8px",
              boxShadow: "0 12px 32px rgba(20, 20, 43, 0.18)",
              py: 1,
            },
          }}
        >
          {allowedCountryStatusActions.map((action) => {
            const currentStatus = String(actionCountry?.status || bid?.status || "won").toLowerCase();
            const isCurrent = currentStatus === action.value;

            return (
              <MenuItem
                key={action.value}
                disabled={statusLoading || isCurrent}
                onClick={() => updateCountryStatus(action.value)}
                sx={{ color: action.color, fontWeight: 600, fontSize: "0.875rem" }}
              >
                {action.label}
                {statusLoading && !isCurrent ? (
                  <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />
                ) : null}
              </MenuItem>
            );
          })}
        </Menu>
      </SoftBox>

      <Footer />
    </DashboardLayout>
  );
}

export default ProjectCountryMetrics;
